


import * as PIXI from 'pixi.js';
import { SpriteLab } from './SpriteLab';
import { AuthModal } from '../auth/AuthModal';
import { auth, logout, subscribeToAuthChanges, hasSession } from '../auth/FirebaseAuth';
import { logDev } from '../utils/LogDev';
import { parseGIF, decompressFrames } from 'gifuct-js';
import { SnapshotData } from './HistoryManager';
import { GuestStorageManager } from './GuestStorageManager';
import { AnimationConfigModal } from './AnimationConfigModal';

import '../../styles/spriteLab/spriteLab.css';
import '../../styles/dialogs/dialogs.css';
import '../../styles/auth/auth.css';
import '../../styles/spriteLab/toolbar.css';
import '../../styles/spriteLab/tabs.css';
import '../../styles/spriteLab/history.css';
import '../../styles/spriteLab/explorer.css';
import '../../styles/spriteLab/layout.css';
import '../../styles/spriteLab/modals.css';


function getBackIconHTML() {
    const base = [
        [0, 0, 0, 1, 0],
        [0, 0, 1, 0, 0],
        [0, 1, 0, 0, 0],
        [1, 0, 0, 0, 0],
        [0, 1, 0, 0, 0],
        [0, 0, 1, 0, 0],
        [0, 0, 0, 1, 0]
    ];
    const scale = 2;
    const rows = (base.length + 2) * scale;
    const cols = (base[0].length + 2) * scale;
    let html = '<div class="pixel-char orange"><div class="pixel-grid">';
    for (let y = 0; y < rows; y++) {
        html += '<div class="pixel-row">';
        for (let x = 0; x < cols; x++) {
            const origY = Math.floor(y / scale) - 1;
            const origX = Math.floor(x / scale) - 1;
            const isOn = origY >= 0 && origY < base.length && origX >= 0 && origX < base[0].length && base[origY][origX];
            html += `<div class="pixel ${isOn ? 'on' : 'off'}"></div>`;
        }
        html += '</div>';
    }
    return html + '</div></div>';
}

export class LifeCycleManager {
    private app: SpriteLab;
    private feedbackQueues = {
        project: new Set<string>(),
        sprite: new Set<string>(),
        sheet: new Set<string>()
    };

    constructor(app: SpriteLab) {
        this.app = app;
    }

    private showFeedback(container: HTMLElement) {
        const check = document.createElement('span');
        check.className = 'sl-rename-success-check';
        check.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>`;

        // Find the name span to place it right after, otherwise append to container
        const nameSpan = container.querySelector('.name-span');
        if (nameSpan && nameSpan.nextSibling) {
            container.insertBefore(check, nameSpan.nextSibling);
        } else {
            container.appendChild(check);
        }

        setTimeout(() => {
            check.classList.add('sl-rename-success-fade');
            setTimeout(() => check.remove(), 1000);
        }, 1500);
    }

    public setupUI() {
        logDev('[LifeCycleManager] Setting up UI...');
        this.app.container.innerHTML = `
            <nav id="navbar">
                <h1 class="nav-left">
                    <button id="sl-back-btn" data-href="${import.meta.env.VITE_HOME_URL || '/'}" aria-label="Back to home">
                        ${getBackIconHTML()}
                    </button>
                    <img src="/logo.svg" alt="Sprite Lab" width="320" height="80" class="nav-logo" fetchpriority="high" />
                </h1>
                <div class="nav-right">
                    <button id="sl-lang-btn" class="sl-btn nav-btn-lang nav-btn" title="${this.app.t('select_language')}" aria-label="${this.app.t('select_language')}">
                        <i class="fa-solid fa-globe" aria-hidden="true"></i>
                        <span id="sl-lang-text">EN</span>
                    </button>
                    <div class="sl-menu-container">
                        <button id="sl-menu-btn" class="sl-btn nav-btn" title="Menu">
                            <i class="fa-solid fa-bars"></i>
                        </button>
                        <div id="sl-dropdown-menu" class="sl-dropdown-menu">
                            <button id="sl-save-btn" class="sl-dropdown-item" title="${this.app.t('menu_save')}">
                                <i class="fa-regular fa-floppy-disk"></i>
                                <span>${this.app.t('menu_save')}</span>
                            </button>
                            <button id="sl-save-all-btn" class="sl-dropdown-item" title="${this.app.t('menu_save_all')}">
                                <i class="fa-solid fa-floppy-disk"></i>
                                <span>${this.app.t('menu_save_all')}</span>
                            </button>
                            <div class="sl-dropdown-divider"></div>
                             <button id="sl-support-btn" class="sl-dropdown-item" title="${this.app.t('menu_support')}">
                                <i class="fa-regular fa-envelope"></i>
                                <span>${this.app.t('menu_support')}</span>
                            </button>
                            <div class="sl-dropdown-divider"></div>
                            <div class="sl-storage-container">
                                <div class="sl-storage-header">
                                    <span class="sl-storage-label">${this.app.t('label_local_storage')}</span>
                                    <span id="sl-storage-text">0.0 / 100 MB</span>
                                </div>
                                <div class="sl-storage-bar-bg">
                                    <div id="sl-storage-bar-fill" class="sl-storage-bar-fill"></div>
                                </div>
                            </div>
                            <div class="sl-dropdown-divider"></div>
                            <span class="sl-version-display">SpriteLab v${APP_VERSION}</span>
                        </div>
                    </div>
                </div>
            </nav>
            <main class="sl-main-layout" id="sl-main-layout">
                <!-- Sidebar -->
                <div id="sl-sidebar-left" class="sl-sidebar-left">
                    <!-- Explorer Panel -->
                    <div id="sl-explorer-panel">
                        <div class="sl-explorer-header">
                            <h2>${this.app.t('explorer_title')}</h2>
                        </div>
                        <div id="sl-explorer-tree" class="sl-explorer-tree">
                            <!-- Explorer Tree Injected Here -->
                        </div>
                    </div>

                    <!-- History Panel -->
                    <div id="sl-history-panel"  class="sl-layout-1">
                        <h3  class="sl-layout-2">${this.app.t('history_title')}</h3>
                        <div id="sl-history-list"  class="sl-layout-3">
                            <!-- History Items Injected Here -->
                        </div>
                    </div>
                </div>

                <!-- Main Content Wrapper -->
                <div class="sl-main-content-wrapper sl-active-panel">
                    <!-- Shared Header: Tabs (Left) + Sprite List (Right) -->
                    <div  class="sl-layout-4">
                        <div class="sl-tabs-nav-container">
                            <button id="sl-tabs-prev" class="sl-tabs-nav-btn" style="display: none;"><i class="fa-solid fa-chevron-left"></i></button>
                            <div id="sl-sheet-tabs-scroll" class="sl-sheet-tabs-scroll">
                                <div id="sl-sheet-tabs"  class="sl-layout-5">
                                    <!-- Tabs will be injected here -->
                                </div>
                            </div>
                            <button id="sl-tabs-next" class="sl-tabs-nav-btn" style="display: none;"><i class="fa-solid fa-chevron-right"></i></button>
                            <button id="sl-add-sheet-btn" title="${this.app.t('action_add_sheet')}"  class="sl-layout-6">+</button>
                        </div>
                        <div  class="sl-layout-7">
                            <!-- File Input for uploads -->
                            <input type="file" id="sl-file-input" accept="image/png, image/gif"  class="sl-layout-8">
                        </div>
                    </div>

                    <!-- No Sprite Selected Message -->
                    <div id="sl-no-selection-msg"  class="sl-layout-9">
                    </div>

                    <!-- Preview Area -->
                    <div id="sl-workspace" class="sl-workspace">
                        <!-- Left: Sprite Sheet View -->
                        <div id="sl-sheet-view-wrapper" class="sl-sheet-view-wrapper">
                            <div  class="sl-layout-10">
                        <div id="sl-sheet-container"  class="sl-layout-11">
                            <!-- Wrapper for Image + Overlay -->
                            <div id="sl-sheet-frame"  class="sl-layout-12">
                                <canvas id="sl-sheet-canvas"  class="sl-layout-13"></canvas>
                                <svg id="sl-sheet-grid" xmlns="http://www.w3.org/2000/svg"  class="sl-layout-14" overflow="visible" style="overflow: visible;">
                                    <line x1="0" y1="33.33%" x2="100%" y2="33.33%" stroke="#3c3c3c" stroke-width="1" vector-effect="non-scaling-stroke" />
                                    <line x1="0" y1="66.66%" x2="100%" y2="66.66%" stroke="#3c3c3c" stroke-width="1" vector-effect="non-scaling-stroke" />
                                </svg>
                                <div id="sl-fine-grid"  class="sl-layout-15"></div>
                            </div>
                            <div id="sl-sheet-loading"  class="sl-layout-16">
                                 <img src="/square.svg" alt="Loading..." width="60" height="60" class="sl-layout-17"/>
                            </div>
                            <div id="sl-sheet-info-container"  class="sl-layout-18">
                                <button id="sl-upload-btn" class="sl-btn sl-layout-19" aria-label="${this.app.t('action_import')}">
                                    <i class="fa-solid fa-upload"></i>
                                    <span class="sl-btn-text">${this.app.t('action_import')}</span>
                                </button>
                                <button id="sl-download-btn" class="sl-btn sl-layout-19" aria-label="${this.app.t('action_export')}">
                                    <i class="fa-solid fa-download"></i>
                                    <span class="sl-btn-text">${this.app.t('action_export')}</span>
                                </button>
                                <div id="sl-sheet-info"  class="sl-layout-20"></div>
                            </div>

                            <!-- Brush Settings Control Box -->
                            <div class="sl-brush-settings sl-layout-21" id="sl-brush-settings" style="display: none;" >
                                <div id="sl-color-config" class="sl-layout-22">
                                    <div  class="sl-layout-23">
                                        <label  class="sl-layout-24">${this.app.t('label_colors')}</label>
                                        <div style="display: flex; align-items: flex-end; gap: 8px;">
                                            <div class="sl-brush-color-container">
                                                <div class="sl-brush-color-primary-wrap" title="Primary Color (Left Click)">
                                                    <div class="sl-custom-color-box sl-chessboard">
                                                        <div id="sl-brush-color" style="background-color: #000000; width: 100%; height: 100%;"></div>
                                                    </div>
                                                </div>
                                                <button id="sl-brush-color-switch" class="sl-color-switch-btn" title="Switch Colors (X)">
                                                    <i class="fa-solid fa-repeat"></i>
                                                </button>
                                                <div class="sl-brush-color-secondary-wrap" title="Secondary Color (Right Click)">
                                                    <div class="sl-custom-color-box sl-chessboard">
                                                        <div id="sl-brush-color-secondary" style="background-color: #ffffff; width: 100%; height: 100%;"></div>
                                                    </div>
                                                </div>
                                            </div>
                                            <button id="sl-eyedropper-btn" class="sl-btn sl-layout-26" title="${this.app.t('tool_eyedropper')}">
                                                <i class="fa-solid fa-eye-dropper"></i>
                                            </button>
                                            <button id="sl-brush-mode-toggle" class="sl-btn sl-layout-26" title="${this.app.t('tool_brush_mode')}">
                                                <i class="fa-solid fa-droplet"></i>
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div id="sl-size-config" class="sl-layout-23" style="display: none;">
                                    <div  class="sl-layout-27">
                                        <label  class="sl-layout-28">${this.app.t('label_size')}</label>
                                        <div id="sl-brush-size-container"  title="Click to enter size manually" class="sl-layout-29">
                                            <span id="sl-brush-size-display"  class="sl-layout-30">1 px</span>
                                            <input type="number" id="sl-brush-size-input" value="1" min="1" max="100"  class="sl-layout-31">
                                        </div>
                                    </div>
                                    <input type="range" id="sl-brush-size" min="1" max="100" value="1" step="1"  class="sl-layout-32">
                                </div>

                                <div id="sl-bucket-config" class="sl-layout-23" style="display: none;">
                                    <div class="sl-layout-27">
                                        <label class="sl-layout-28">${this.app.t('label_tolerance')}</label>
                                        <div id="sl-bucket-tolerance-container" title="Double click to enter tolerance manually" class="sl-layout-29">
                                            <span id="sl-bucket-tolerance-display" class="sl-layout-30">0%</span>
                                            <input type="number" id="sl-bucket-tolerance-input" value="0" min="0" max="100" class="sl-layout-31">
                                        </div>
                                    </div>
                                    <input type="range" id="sl-bucket-tolerance" min="0" max="100" value="0" step="1" class="sl-layout-32">
                                </div>

                            </div>

                            <div id="sl-trash-bin"  class="sl-layout-33">
                                <i id="sl-trash-icon-closed" class="fa-solid fa-trash-can" style="color: #f85149; font-size: 1.5rem;"></i>
                                <i id="sl-trash-icon-open" class="fa-regular fa-trash-can" style="color: #f85149; font-size: 1.5rem; display: none;"></i>
                            </div>

                            <!-- Drawing Toolbar -->
                            <div class="sl-toolbar sl-layout-21" id="sl-toolbar-panel" >
                                <div class="sl-toolbar-grip" id="sl-toolbar-grip" title="Drag to move toolbox">
                                    <i class="fa-solid fa-grip-lines"></i>
                                </div>
                                <!-- Mouse Tool -->
                                <button class="sl-tool-btn active" data-tool="mouse" title="${this.app.t('tool_mouse')}">
                                    <i class="fa-solid fa-arrow-pointer"></i>
                                </button>
                                <div class="sl-tool-separator"></div>

                                <!-- Brush Tool -->
                                <button class="sl-tool-btn" data-tool="brush" title="${this.app.t('tool_brush')}">
                                    <i class="fa-solid fa-paintbrush"></i>
                                </button>

                                <!-- Eraser Tool -->
                                <button class="sl-tool-btn" data-tool="eraser" title="${this.app.t('tool_eraser')}">
                                    <i class="fa-solid fa-eraser"></i>
                                </button>

                                <!-- Bucket Tool -->
                                <button class="sl-tool-btn" data-tool="bucket" title="${this.app.t('tool_bucket')}">
                                    <i class="fa-solid fa-fill-drip"></i>
                                </button>
                                <div class="sl-tool-separator"></div>

                                <!-- Grid Toggle -->
                                <button class="sl-tool-btn" data-tool="grid" title="${this.app.t('tool_grid')}">
                                    <i class="fa-solid fa-table-cells"></i>
                                </button>
                                <!-- Rotate Tool -->
                                <button class="sl-tool-btn" id="sl-action-rotate" title="${this.app.t('tool_rotate')}">
                                    <i class="fa-solid fa-rotate"></i>
                                </button>

                                <!-- Flip Horizontal Tool -->
                                <button class="sl-tool-btn" id="sl-action-flip-h" title="${this.app.t('tool_flip_h')}">
                                    <i class="fa-solid fa-right-left"></i>
                                </button>

                                <!-- Flip Vertical Tool -->
                                <button class="sl-tool-btn" id="sl-action-flip-v" title="${this.app.t('tool_flip_v')}">
                                    <i class="fa-solid fa-up-down"></i>
                                </button>

                                <!-- Transpose Tool -->
                                <button class="sl-tool-btn" id="sl-action-transpose-real" title="${this.app.t('tool_transpose')}">
                                    <i class="fa-solid fa-shuffle"></i>
                                </button>
                            </div>


                        </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Right: Animation View & Config -->
            <div id="sl-sidebar-right" class="sl-sidebar-right">
                        <div class="sl-animation-panel sl-layout-36" >
                             <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px;">
                                 <h3 class="sl-layout-37" style="margin-bottom: 0;">${this.app.t('animation_title')}</h3>
                                 <button type="button" id="sl-anim-config-btn" title="${this.app.t('tool_anim_config')}" class="sl-btn nav-btn" aria-label="Animation Configuration" style="padding: 4px 8px; min-width: auto; height: auto; border: none; background: transparent; font-size: 1.2rem;">
                                     <i class="fa-solid fa-gear"></i>
                                 </button>
                             </div>
                             <div id="sl-preview-wrap"  class="sl-layout-38">
                               <!-- Canvas goes here -->
                            </div>
                            <!-- Hidden: keeps JS reference alive, value synced via JS -->
                            <input type="text" id="sl-bg-color-text" value="#00000000" style="display:none">
                        </div>

                         <!-- Config Panel -->
                         <div  class="sl-layout-45">

                            <h3  class="sl-layout-37">${this.app.t('config_title')}</h3>

                            <div  class="sl-layout-46">
                                <div>
                                    <label  class="sl-layout-47">${this.app.t('label_width')}</label>
                                    <div  class="sl-layout-48">
                                        <input type="number" id="sl-meta-width" value="32" class="sl-config-input sl-layout-49" aria-label="Frame width">
                                        <span  class="sl-layout-50">px</span>
                                    </div>
                                </div>
                                <div>
                                    <label  class="sl-layout-47">${this.app.t('label_height')}</label>
                                    <div  class="sl-layout-48">
                                        <input type="number" id="sl-meta-height" value="32" class="sl-config-input sl-layout-49" aria-label="Frame height">
                                        <span  class="sl-layout-50">px</span>
                                    </div>
                                </div>
                                <div>
                                    <label  class="sl-layout-47">${this.app.t('label_frames')}</label>
                                    <div  class="sl-layout-48">
                                        <input type="number" id="sl-meta-frames" value="3" min="1" max="100" class="sl-config-input sl-layout-49" aria-label="Number of frames">
                                    </div>
                                </div>

                                <div  class="sl-layout-55"></div>

                                <!-- Resize Section -->
                                <div  class="sl-layout-56">
                                    <div  class="sl-layout-57">
                                        <label  class="sl-layout-52">${this.app.t('label_new_width')}</label>
                                        <div  class="sl-layout-48">
                                            <input type="number" id="sl-scale-width" value="32" class="sl-config-input sl-layout-49" aria-label="Scale new width">
                                            <span  class="sl-layout-50">px</span>
                                        </div>
                                    </div>
                                    <div  class="sl-layout-57">
                                        <label  class="sl-layout-52">${this.app.t('label_new_height')}</label>
                                        <div  class="sl-layout-48">
                                            <input type="number" id="sl-scale-height" value="32" class="sl-config-input sl-layout-49" aria-label="Scale new height">
                                            <span  class="sl-layout-50">px</span>
                                        </div>
                                    </div>
                                    <button id="sl-scale-apply" class="sl-btn sl-layout-58" >${this.app.t('label_apply')}</button>
                                </div>
                        </div>
                    </div>
                </div>
            </main>

            <!-- Modals -->
            <!-- Save All Modal -->
            <div id="sl-modal-save-all"  class="sl-layout-59">
                <div  class="sl-layout-60">
                    <h3  class="sl-layout-61">${this.app.t('modal_save_all_title')}</h3>
                    <p  class="sl-layout-62">${this.app.t('modal_save_all_desc')}</p>

                    <div id="sl-save-all-list"  class="sl-layout-63">
                        <!-- Checkboxes injected here -->
                    </div>

                    <div  class="sl-layout-64">
                        <button id="sl-save-all-cancel" class="sl-btn-animate sl-layout-65" >${this.app.t('action_cancel')}</button>
                        <button id="sl-save-all-confirm" class="sl-btn-animate sl-layout-66" >${this.app.t('modal_save_selected')}</button>
                    </div>
                </div>
            </div>

            <!-- Create Sprite Modal -->
            <div id="sl-modal-create-sprite"  class="sl-layout-59">
                <div  class="sl-layout-67">
                    <h3  class="sl-layout-61">${this.app.t('modal_create_sprite_title')}</h3>
                    <input type="text" id="sl-new-sprite-name" placeholder="${this.app.t('placeholder_sprite_name')}"  class="sl-layout-68">
                    <div  class="sl-layout-64">
                        <button id="sl-create-sprite-cancel"  class="sl-layout-65">${this.app.t('action_cancel')}</button>
                        <button id="sl-create-sprite-confirm"  class="sl-layout-66">${this.app.t('action_create')}</button>
                    </div>
                </div>
            </div>

            <!-- Create Project Modal -->
            <div id="sl-modal-create-project"  class="sl-layout-59">
                <div  class="sl-layout-67">
                    <h3  class="sl-layout-61">${this.app.t('modal_create_project_title')}</h3>
                    <input type="text" id="sl-new-project-name" placeholder="${this.app.t('placeholder_project_name')}"  class="sl-layout-68">
                    <div  class="sl-layout-64">
                        <button id="sl-create-project-cancel"  class="sl-layout-65">${this.app.t('action_cancel')}</button>
                        <button id="sl-create-project-confirm"  class="sl-layout-66">${this.app.t('action_create')}</button>
                    </div>
                </div>
            </div>

            <!-- Add Sheet Modal -->
            <div id="sl-modal-add-sheet"  class="sl-layout-59">
                <div  class="sl-layout-67">
                    <h3  class="sl-layout-61">${this.app.t('modal_add_sheet_title')}</h3>
                    <input type="text" id="sl-new-sheet-name" placeholder="${this.app.t('placeholder_sheet_name')}"  class="sl-layout-68">
                    <div  class="sl-layout-64">
                        <button id="sl-add-sheet-cancel"  class="sl-layout-65">${this.app.t('action_cancel')}</button>
                        <button id="sl-add-sheet-confirm"  class="sl-layout-66">${this.app.t('action_add')}</button>
                    </div>
                </div>
            </div>

            <!-- Rename Sheet Modal -->
            <div id="sl-modal-rename-sheet"  class="sl-layout-59">
                <div  class="sl-layout-67">
                    <h3  class="sl-layout-61">${this.app.t('modal_rename_sheet_title')}</h3>
                    <input type="text" id="sl-rename-sheet-input" placeholder="${this.app.t('placeholder_new_name')}"  class="sl-layout-68">
                    <div  class="sl-layout-64">
                        <button id="sl-rename-sheet-cancel"  class="sl-layout-65">${this.app.t('action_cancel')}</button>
                        <button id="sl-rename-sheet-confirm"  class="sl-layout-66">${this.app.t('action_rename')}</button>
                    </div>
                </div>
            </div>

             <!-- Rename Sprite Modal -->
            <div id="sl-modal-rename-sprite"  class="sl-layout-59">
                <div  class="sl-layout-67">
                    <h3  class="sl-layout-61">${this.app.t('modal_rename_sprite_title')}</h3>
                    <input type="text" id="sl-rename-sprite-input" placeholder="${this.app.t('placeholder_new_name')}"  class="sl-layout-68">
                    <div  class="sl-layout-64">
                        <button id="sl-rename-sprite-cancel"  class="sl-layout-65">${this.app.t('action_cancel')}</button>
                        <button id="sl-rename-sprite-confirm"  class="sl-layout-66">${this.app.t('action_rename')}</button>
                    </div>
                </div>
            </div>

            <!-- Delete Sprite Modal -->
            <div id="sl-modal-delete-sprite"  class="sl-layout-59">
                <div  class="sl-layout-67">
                    <h3  class="sl-layout-69">${this.app.t('modal_delete_sprite_title')}</h3>
                    <p  class="sl-layout-70">${this.app.t('confirm_delete_sprite')} (<span id="sl-delete-sprite-name-disp"  class="sl-layout-71"></span>)</p>
                    <div  class="sl-layout-72">
                        <button id="sl-delete-sprite-cancel" class="sl-btn-animate sl-layout-65" >${this.app.t('action_cancel')}</button>
                        <button id="sl-delete-sprite-confirm" class="sl-btn-animate sl-layout-73" >${this.app.t('action_delete')}</button>
                    </div>
                </div>
            </div>

            <!-- Delete Project Modal -->
            <div id="sl-modal-delete-project"  class="sl-layout-59">
                <div  class="sl-layout-67">
                     <h3  class="sl-layout-69">${this.app.t('modal_delete_project_title')}</h3>
                     <p  class="sl-layout-70">${this.app.t('confirm_delete_project')} (<span id="sl-delete-project-name-disp"  class="sl-layout-71"></span>)</p>
                     <div  class="sl-layout-72">
                         <button id="sl-delete-project-cancel" class="sl-btn-animate sl-layout-65" >${this.app.t('action_cancel')}</button>
                         <button id="sl-delete-project-confirm" class="sl-btn-animate sl-layout-73" >${this.app.t('action_delete')}</button>
                     </div>
                </div>
            </div>

             <!-- Delete Sheet Modal -->
            <div id="sl-modal-delete-sheet"  class="sl-layout-59">
                <div  class="sl-layout-67">
                    <h3  class="sl-layout-69">${this.app.t('modal_delete_sheet_title')}</h3>
                    <p  class="sl-layout-70">${this.app.t('confirm_delete_sheet')} (<span id="sl-delete-sheet-name-disp"  class="sl-layout-71"></span>)</p>
                    <div  class="sl-layout-72">
                        <button id="sl-delete-sheet-cancel" class="sl-btn-animate sl-layout-65" >${this.app.t('action_cancel')}</button>
                        <button id="sl-delete-sheet-confirm" class="sl-btn-animate sl-layout-73" >${this.app.t('action_delete')}</button>
                    </div>
                </div>
            </div>

            <!-- Overwrite Warning Modal -->
            <div id="sl-modal-overwrite-warning"  class="sl-layout-59">
                <div  class="sl-layout-74">
                    <h3  class="sl-layout-75">Unsaved Changes</h3>
                    <p  class="sl-layout-70">Loading a new file will overwrite your current work. Are you sure you want to proceed?</p>
                    <div  class="sl-layout-72">
                        <button id="sl-overwrite-cancel" class="sl-btn-animate sl-layout-65" >Cancel</button>
                        <button id="sl-overwrite-confirm" class="sl-btn-animate sl-layout-76" >Yes, Overwrite</button>
                    </div>
                </div>
            </div>
            <!-- Unsaved Changes Warning Modal -->
            <div id="sl-modal-unsaved-changes"  class="sl-layout-77">
                <div  class="sl-layout-74">
                    <h3  class="sl-layout-75">Unsaved Changes</h3>
                    <p  class="sl-layout-70">You have unsaved changes. Are you sure you want to leave? Your changes will be lost.</p>
                    <div  class="sl-layout-72">
                        <button id="sl-unsaved-cancel" class="sl-btn-animate sl-layout-65" >Cancel</button>
                        <button id="sl-unsaved-confirm" class="sl-btn-animate sl-layout-73" >Leave</button>
                    </div>
                </div>
            </div>
            </div>

            <!-- Mobile Navigation -->
            <nav class="sl-mobile-nav">
                <button id="sl-prev-panel" class="sl-nav-arrow" title="Previous Panel">
                    <i class="fa-solid fa-chevron-left"></i>
                </button>
                <div class="sl-panel-info">
                    <span id="sl-current-panel-label" class="sl-panel-label">FILES</span>
                    <div class="sl-panel-dots">
                        <div class="sl-nav-dot active" data-idx="0"></div>
                        <div class="sl-nav-dot" data-idx="1"></div>
                        <div class="sl-nav-dot" data-idx="2"></div>
                    </div>
                </div>
                <button id="sl-next-panel" class="sl-nav-arrow" title="Next Panel">
                    <i class="fa-solid fa-chevron-right"></i>
                </button>
            </nav>
        `;

        this.app.explorerTree = this.app.container.querySelector('#sl-explorer-tree') as HTMLDivElement;

        this.setupListeners();
        this.setupAuth();
        this.setupColorPicker();
        this.setupPixi();
    }

    private async setupColorPicker() {
        if (!this.app.colorPicker) {
            const { ColorPicker } = await import('./ColorPicker');
            this.app.colorPicker = new ColorPicker(this.app.container, this.app.t.bind(this.app));
            this.app.colorPicker.onEyeDropperFallback = () => {
                this.app.colorPicker.hideForPicker();
                this.app.editorManager.switchTool('picker');
            };
            this.app.colorPicker.onPaletteChange = (palette: string[]) => {
                const key = `${this.app.projectId}:${this.app.itemId}:${this.app.currentSheet}`;
                const sheetData = this.app.sessionSheets.get(key);
                if (sheetData) {
                    sheetData.palette = [...palette];
                    this.app.checkDirty();
                    this.app.updateButtonStates();
                }
            };
        }
    }

    private setupListeners() {
        const fileInput = this.app.container.querySelector('#sl-file-input') as HTMLInputElement;
        fileInput?.addEventListener('change', () => {
            const file = fileInput.files?.[0];
            logDev(`[LifeCycle] File input change: ${file?.name || 'none'}`);
            if (!file) return;

            const processFile = () => {
                if (file.type === 'image/gif') {
                    this.processGifFile(file);
                    return;
                }

                                const reader = new FileReader();
                reader.onload = (evt) => {
                    let base64 = evt.target?.result as string;
                    const img = new Image();
                    img.onload = async () => {
                        const iw = img.width;
                        const ih = img.height;

                        // Auto-convert magenta (#FF00FF) to transparent
                        const tmpCanvas = document.createElement('canvas');
                        tmpCanvas.width = iw;
                        tmpCanvas.height = ih;
                        const tmpCtx = tmpCanvas.getContext('2d');
                        if (tmpCtx) {
                            tmpCtx.drawImage(img, 0, 0);
                            const imgData = tmpCtx.getImageData(0, 0, iw, ih);
                            const data = imgData.data;
                            let hasMagenta = false;
                            for (let i = 0; i < data.length; i += 4) {
                                if (data[i] === 255 && data[i+1] === 0 && data[i+2] === 255) {
                                    data[i+3] = 0;
                                    hasMagenta = true;
                                }
                            }
                            if (hasMagenta) {
                                tmpCtx.putImageData(imgData, 0, 0);
                                base64 = tmpCanvas.toDataURL();
                                const nextImg = new Image();
                                nextImg.onload = async () => {
                                    await processImg(nextImg, iw, ih, base64);
                                };
                                nextImg.src = base64;
                                return;
                            }
                        }
                        
                        await processImg(img, iw, ih, base64);
                    };
                    img.src = base64;
                };

                const processImg = async (imgObj: HTMLImageElement, iw: number, ih: number, b64: string) => {
                        // Common sprite frame sizes (largest-first for priority)
                        const commonSizes = [512, 256, 192, 128, 96, 64, 48, 32, 24, 16, 8];
                        const bestSize = (strip: number, other: number): number => {
                            // Prefer sizes that divide BOTH dimensions (square frames)
                            for (const s of commonSizes) {
                                if (s <= Math.min(strip, other) && strip % s === 0 && other % s === 0) return s;
                            }
                            // Fall back: just divides the strip dimension
                            for (const s of commonSizes) {
                                if (s <= strip && strip % s === 0) return s;
                            }
                            return strip;
                        };

                        let newFrames = 1;
                        let newOrientation = 'horizontal';
                        let newWidth = iw;
                        let newHeight = ih;

                        if (iw === ih) {
                            // Square -> single frame
                            newWidth = iw; newHeight = ih; newFrames = 1; newOrientation = 'horizontal';
                        } else if (iw > ih) {
                            // Wide image -> horizontal strip
                            newOrientation = 'horizontal';
                            if (iw % ih === 0) {
                                // Perfect square frames (e.g. 128x32 -> 4 frames of 32x32)
                                newWidth = ih; newHeight = ih;
                                newFrames = iw / ih;
                            } else {
                                newWidth = bestSize(iw, ih);
                                newHeight = ih;
                                newFrames = Math.max(1, Math.round(iw / newWidth));
                            }
                        } else {
                            // Tall image -> vertical strip
                            newOrientation = 'vertical';
                            if (ih % iw === 0) {
                                // Perfect square frames (e.g. 32x128 -> 4 frames of 32x32)
                                newWidth = iw; newHeight = iw;
                                newFrames = ih / iw;
                            } else {
                                newWidth = iw;
                                newHeight = bestSize(ih, iw);
                                newFrames = Math.max(1, Math.round(ih / newHeight));
                            }
                        }

                        logDev(`[LifeCycle] Format guess: ${iw}x${ih} -> frame ${newWidth}x${newHeight}, ${newFrames} frames, ${newOrientation}`);

                        (this.app.container.querySelector('#sl-meta-width') as HTMLInputElement).value = newWidth.toString();
                        (this.app.container.querySelector('#sl-meta-height') as HTMLInputElement).value = newHeight.toString();
                        (this.app.container.querySelector('#sl-meta-frames') as HTMLInputElement).value = newFrames.toString();
                        const orientSelect = this.app.container.querySelector('#sl-meta-orientation') as HTMLSelectElement;
                        if (orientSelect) orientSelect.value = newOrientation;

                        await this.app.updatePreview(b64);
                        this.app.historyManager.takeSnapshot();
                };
                reader.readAsDataURL(file);
            };

            if (this.app.hasContent()) {
                const modal = this.app.container.querySelector('#sl-modal-overwrite-warning') as HTMLElement;
                modal.style.display = 'flex';
                const confirmBtn = this.app.container.querySelector('#sl-overwrite-confirm') as HTMLButtonElement;
                const cancelBtn = this.app.container.querySelector('#sl-overwrite-cancel') as HTMLButtonElement;
                const cleanup = () => {
                    confirmBtn.onclick = null;
                    cancelBtn.onclick = null;
                    modal.style.display = 'none';
                    if (!confirmed) fileInput.value = '';
                };
                let confirmed = false;
                confirmBtn.onclick = () => { confirmed = true; cleanup(); processFile(); };
                cancelBtn.onclick = () => { confirmed = false; cleanup(); };
            } else {
                processFile();
            }
        });

        // Load Sprite button → triggers the hidden file input
        const uploadBtn = this.app.container.querySelector('#sl-upload-btn') as HTMLButtonElement;
        uploadBtn?.addEventListener('click', () => {
            logDev('[LifeCycle] Load Sprite clicked');
            fileInput?.click();
        });

        // Download button → exports current sheet as PNG
        const downloadBtn = this.app.container.querySelector('#sl-download-btn') as HTMLButtonElement;
        downloadBtn?.addEventListener('click', () => {
            logDev('[LifeCycle] Download clicked');
            this.app.handleDownload();
        });
        // Hamburger Menu Toggle
        const menuBtn = this.app.container.querySelector('#sl-menu-btn') as HTMLButtonElement;
        const dropMenu = this.app.container.querySelector('#sl-dropdown-menu') as HTMLDivElement;

        if (menuBtn && dropMenu) {
            menuBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                dropMenu.classList.toggle('show');
                if (dropMenu.classList.contains('show')) {
                    this.updateStorageVisuals();
                }
            });

            // Close dropdown when clicking outside
            document.addEventListener('click', (e) => {
                const target = e.target as HTMLElement;
                if (!target.closest('.sl-menu-container') && dropMenu.classList.contains('show')) {
                    dropMenu.classList.remove('show');
                }

                if (!target.closest('.sl-item-menu')) {
                    document.querySelectorAll('.sl-item-menu .sl-dropdown-menu').forEach(m => {
                        m.classList.remove('show');
                        (m as HTMLElement).style.position = '';
                        (m as HTMLElement).style.top = '';
                        (m as HTMLElement).style.left = '';
                    });
                }

                // Close color picker when clicking outside
                if (this.app.colorPicker && this.app.colorPicker.isOpen()) {
                    const pickerEl = this.app.colorPicker.getElement ? this.app.colorPicker.getElement() : document.querySelector('#sl-color-picker');
                    const isTrigger = target.closest('.sl-color-btn, .sl-color-box, .sl-preview-bg-pick, #sl-bg-color');
                    if (!pickerEl?.contains(target) && !isTrigger) {
                        this.app.colorPicker.close();
                    }
                }
            });
        }

        // Color Picker listeners
        const primaryColorBtn = this.app.container.querySelector('#sl-brush-color') as HTMLElement;
        const secondaryColorBtn = this.app.container.querySelector('#sl-brush-color-secondary') as HTMLElement;
        const animConfigBtn = this.app.container.querySelector('#sl-anim-config-btn') as HTMLElement;

        primaryColorBtn?.addEventListener('click', (e) => {
            e.stopPropagation();
            if (this.app.colorPicker?.isOpen() && this.app.colorPicker.getActiveTrigger() === primaryColorBtn) {
                this.app.colorPicker.close();
            } else {
                const rect = primaryColorBtn.getBoundingClientRect();
                this.app.colorPicker?.open(primaryColorBtn, rect.left, rect.bottom + 8, this.app.toolState.brush.color, (color) => {
                    this.app.toolState.brush.color = color;
                    primaryColorBtn.style.backgroundColor = color;
                    this.app.updateCursor();
                }, () => {
                    this.app.historyManager.takeSnapshot();
                });
            }
        });

        secondaryColorBtn?.addEventListener('click', (e) => {
            e.stopPropagation();
            if (this.app.colorPicker?.isOpen() && this.app.colorPicker.getActiveTrigger() === secondaryColorBtn) {
                this.app.colorPicker.close();
            } else {
                const rect = secondaryColorBtn.getBoundingClientRect();
                this.app.colorPicker?.open(secondaryColorBtn, rect.left, rect.bottom + 8, this.app.toolState.brush.secondaryColor, (color) => {
                    this.app.toolState.brush.secondaryColor = color;
                    secondaryColorBtn.style.backgroundColor = color;
                }, () => {
                    this.app.historyManager.takeSnapshot();
                });
            }
        });

        animConfigBtn?.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const modal = new AnimationConfigModal(this.app);
            modal.show();
        });

        // Support Button
        const supportBtn = this.app.container.querySelector('#sl-support-btn') as HTMLButtonElement;
        supportBtn?.addEventListener('click', () => {
            logDev('[LifeCycle] Support clicked');
            if (dropMenu) dropMenu.classList.remove('show');

            // Handle opening the support modal
            // This is usually handled by the main App class to manage activeModal state
            const event = new CustomEvent('open-support-modal');
            window.dispatchEvent(event);
        });


        this.setupModalListeners();
    }

    private async updateStorageVisuals() {
        const mb = await GuestStorageManager.getDatabaseSizeInMB();
        const textLabel = this.app.container.querySelector('#sl-storage-text') as HTMLElement;
        const barFill = this.app.container.querySelector('#sl-storage-bar-fill') as HTMLElement;

        if (textLabel && barFill) {
            textLabel.innerText = `${mb.toFixed(1)} / 100 MB`;

            // Calculate percentage used of 100MB
            let percent = (mb / 100) * 100;
            if (percent > 100) percent = 100;

            barFill.style.width = `${percent}%`;

            // Change color based on severity
            if (percent > 95) {
                barFill.style.backgroundColor = '#f85149'; // Red danger
                barFill.style.boxShadow = '0 0 8px rgba(248, 81, 73, 0.4)';
            } else if (percent > 80) {
                barFill.style.backgroundColor = '#d29922'; // Yellow warning
                barFill.style.boxShadow = '0 0 8px rgba(210, 153, 34, 0.4)';
            } else {
                barFill.style.backgroundColor = '#6366f1'; // Default primary
                barFill.style.boxShadow = '0 0 8px rgba(99, 102, 241, 0.4)';
            }
        }
    }

    public setupModalListeners() {
        // Helper for Enter/Esc keys
        const setupInputKeys = (inputId: string, confirmId: string, cancelId: string) => {
            const input = this.app.container.querySelector(inputId) as HTMLInputElement;
            const confirmBtn = this.app.container.querySelector(confirmId) as HTMLButtonElement;
            const cancelBtn = this.app.container.querySelector(cancelId) as HTMLButtonElement;

            if (input) {
                input.addEventListener('keydown', (e) => {
                    if (e.key === 'Enter') {
                        e.preventDefault();
                        if (confirmBtn) confirmBtn.click();
                    } else if (e.key === 'Escape') {
                        e.preventDefault();
                        if (cancelBtn) cancelBtn.click();
                    }
                });
            }
        };

        setupInputKeys('#sl-new-sprite-name', '#sl-create-sprite-confirm', '#sl-create-sprite-cancel');
        setupInputKeys('#sl-new-project-name', '#sl-create-project-confirm', '#sl-create-project-cancel');
        setupInputKeys('#sl-new-sheet-name', '#sl-add-sheet-confirm', '#sl-add-sheet-cancel');
        setupInputKeys('#sl-rename-sheet-input', '#sl-rename-sheet-confirm', '#sl-rename-sheet-cancel');
        setupInputKeys('#sl-rename-sprite-input', '#sl-rename-sprite-confirm', '#sl-rename-sprite-cancel');

        // Add Sheet Tab Button (+)
        const addSheetBtn = this.app.container.querySelector('#sl-add-sheet-btn');
        if (addSheetBtn) {
            addSheetBtn.addEventListener('click', () => {
                if (!this.app.itemId) return alert(this.app.t('err_select_sprite'));
                const modal = this.app.container.querySelector('#sl-modal-add-sheet') as HTMLElement;
                if (modal) {
                    modal.style.display = 'flex';
                    const input = this.app.container.querySelector('#sl-new-sheet-name') as HTMLInputElement;
                    if (input) input.focus();
                }
            });
        }

        // Global Esc for Color Picker and Modals
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') {
                // Close Color Picker
                if (this.app.colorPicker?.isOpen()) {
                    this.app.colorPicker.close();
                }

                // Close all interactive modals
                const modals = this.app.container.querySelectorAll('.sl-layout-59, #sl-modal-unsaved-changes');
                modals.forEach((m: any) => {
                    if (m.style.display === 'flex') m.style.display = 'none';
                });
            } else if (e.key === 'Delete') {
                // Ignore if input is focused
                const active = document.activeElement;
                if (active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement) return;

                // Priority 1: Clear Floating Paste
                if (this.app.pastedData) {
                    this.app.pastedData = null;
                    this.app.updatePasteOverlay();

                    // Also refresh the tool preview (e.g. brush circle)
                    const canvas = this.app.container.querySelector('#sl-sheet-canvas') as HTMLCanvasElement;
                    if (canvas && this.app.lastSheetX !== -1) {
                        this.app.renderToolPreview(this.app.lastSheetX, this.app.lastSheetY, canvas, this.app.dataCanvas);
                    }
                    return;
                }

                // Priority 2: Clear Selected Frame
                if (this.app.selectedFrame) {
                    const wInput = this.app.container.querySelector('#sl-meta-width') as HTMLInputElement;
                    const hInput = this.app.container.querySelector('#sl-meta-height') as HTMLInputElement;
                    const fw = parseInt(wInput.value) || 32;
                    const fh = parseInt(hInput.value) || 32;

                    const ctx = this.app.dataCanvas.getContext('2d');
                    if (ctx) {
                        ctx.clearRect(this.app.selectedFrame.col * fw, this.app.selectedFrame.row * fh, fw, fh);
                        this.app.currentBase64 = this.app.dataCanvas.toDataURL();
                        this.app.updatePreview(this.app.currentBase64, false);
                        this.app.historyManager.takeSnapshot();
                        logDev('[LifeCycle] Delete key: Cleared selected frame');
                    }
                    return;
                }
                // Priority 3: Delete Current Sheet
                if (this.app.itemId && this.app.currentSheet) {
                    this.showConfirmModal('sheet', this.app.currentSheet, async () => {
                        const states = this.app.parsedSheets;
                        const isLastSheet = states.length <= 1;

                        if (!auth.currentUser) {
                            try {
                                const key = `${this.app.projectId}:${this.app.itemId}:${this.app.currentSheet}`;
                                await GuestStorageManager.deleteSheet(key);
                                this.app.sessionSheets.delete(key);
                                
                                if (isLastSheet) {
                                    const newSheetName = 'main';
                                    const newKey = `${this.app.projectId}:${this.app.itemId}:${newSheetName}`;
                                    const gw = parseInt((document.getElementById('sl-meta-width') as HTMLInputElement)?.value) || 32;
                                    const gh = parseInt((document.getElementById('sl-meta-height') as HTMLInputElement)?.value) || 32;
                                    const tmpCanvas = document.createElement('canvas');
                                    tmpCanvas.width = gw; tmpCanvas.height = gh;
                                    const blankB64 = tmpCanvas.toDataURL();
                                    await GuestStorageManager.saveSheet({
                                        key: newKey,
                                        base64: blankB64,
                                        originalBase64: blankB64,
                                        width: gw, height: gh, frames: 1, orientation: 'horizontal',
                                        animation: 'sequence', interval: 0.2,
                                        palette: [...(this.app.colorPicker?.getPalette() || [])],
                                        savedAt: Date.now()
                                    });
                                    this.app.currentSheet = newSheetName;
                                } else {
                                    const next = states.find(s => s !== this.app.currentSheet);
                                    this.app.currentSheet = next || 'main';
                                }
                                
                                await this.app.loadSpriteSheets(this.app.itemId);
                                this.app.checkSpriteAvailability(true);
                            } catch (err) {
                                console.error('[SpriteLab] IDB deleteSheet failed:', err);
                            }
                            return;
                        }

                        const success = await this.app.apiManager.deleteSheet(this.app.projectId, this.app.itemId, this.app.currentSheet);
                        if (success) {
                            this.app.sessionSheets.delete(`${this.app.projectId}:${this.app.itemId}:${this.app.currentSheet}`);
                            
                            if (isLastSheet) {
                                const newSheetName = 'main';
                                const idToken = await auth.currentUser.getIdToken();
                                const apiUrl = (import.meta as any).env.VITE_API_URL || '';
                                const gw = parseInt((document.getElementById('sl-meta-width') as HTMLInputElement)?.value) || 32;
                                const gh = parseInt((document.getElementById('sl-meta-height') as HTMLInputElement)?.value) || 32;
                                await fetch(`${apiUrl}/api/sprite-lab/projects/${this.app.projectId}/sprites/${this.app.itemId}/sheets`, {
                                    method: 'POST',
                                    headers: { 'Authorization': `Bearer ${idToken}`, 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ name: newSheetName, width: gw, height: gh, frames: 1, orientation: 'horizontal' })
                                });
                                this.app.currentSheet = newSheetName;
                            } else {
                                const next = states.find(s => s !== this.app.currentSheet);
                                this.app.currentSheet = next || 'main';
                            }
                            
                            await this.app.loadSpriteSheets(this.app.itemId);
                            this.app.checkSpriteAvailability(true);
                        }
                    });
                    return;
                }

                // Priority 4: Delete Sprite/Project (Lower priority now)
                if (this.app.itemId) {
                    (this.app.container.querySelector('#sl-delete-sprite-name-disp') as HTMLElement).innerText = this.app.itemId;
                    (this.app.container.querySelector('#sl-modal-delete-sprite') as HTMLElement).style.display = 'flex';
                } else if (this.app.projectId && this.app.projectId !== 'main') {
                    (this.app.container.querySelector('#sl-delete-project-name-disp') as HTMLElement).innerText = this.app.projectId;
                    (this.app.container.querySelector('#sl-modal-delete-project') as HTMLElement).style.display = 'flex';
                }
            }
        });

        // Create Sprite
        this.app.container.querySelector('#sl-create-sprite-cancel')!.addEventListener('click', () => {
            (this.app.container.querySelector('#sl-modal-create-sprite') as HTMLElement).style.display = 'none';
        });

        // Create Project Cancel
        this.app.container.querySelector('#sl-create-project-cancel')!.addEventListener('click', () => {
            (this.app.container.querySelector('#sl-modal-create-project') as HTMLElement).style.display = 'none';
        });

        this.app.container.querySelector('#sl-create-sprite-confirm')!.addEventListener('click', () => {
            logDev('[LifeCycle] Triggering handleCreateSprite');
            this.app.handleCreateSprite();
        });

        this.app.container.querySelector('#sl-create-project-confirm')!.addEventListener('click', () => {
            logDev('[LifeCycle] Triggering handleCreateProject');
            this.app.handleCreateProject();
        });

        // Add Sheet
        this.app.container.querySelector('#sl-add-sheet-cancel')!.addEventListener('click', () => {
            (this.app.container.querySelector('#sl-modal-add-sheet') as HTMLElement).style.display = 'none';
        });
        this.app.container.querySelector('#sl-add-sheet-confirm')!.addEventListener('click', () => this.app.handleAddSheet());

        // Rename Sheet
        this.app.container.querySelector('#sl-rename-sheet-cancel')!.addEventListener('click', () => {
            (this.app.container.querySelector('#sl-modal-rename-sheet') as HTMLElement).style.display = 'none';
        });
        this.app.container.querySelector('#sl-rename-sheet-confirm')!.addEventListener('click', () => this.app.handleRenameSheet());

        // Rename Sprite
        this.app.container.querySelector('#sl-rename-sprite-cancel')!.addEventListener('click', () => {
            (this.app.container.querySelector('#sl-modal-rename-sprite') as HTMLElement).style.display = 'none';
        });
        this.app.container.querySelector('#sl-rename-sprite-confirm')!.addEventListener('click', () => this.app.handleRenameSprite());

        // Delete Sprite
        this.app.container.querySelector('#sl-delete-sprite-cancel')!.addEventListener('click', () => {
            (this.app.container.querySelector('#sl-modal-delete-sprite') as HTMLElement).style.display = 'none';
        });

        // Delete Project
        this.app.container.querySelector('#sl-delete-project-cancel')!.addEventListener('click', () => {
            (this.app.container.querySelector('#sl-modal-delete-project') as HTMLElement).style.display = 'none';
        });

        // Delete Sheet
        this.app.container.querySelector('#sl-delete-sheet-cancel')!.addEventListener('click', () => {
            (this.app.container.querySelector('#sl-modal-delete-sheet') as HTMLElement).style.display = 'none';
        });


        const bgColorText = this.app.container.querySelector('#sl-bg-color-text') as HTMLInputElement;
        const updateBg = () => this.app.editorManager.updateEnvironment();

        window.addEventListener('sl-bg-color-change', ((e: CustomEvent) => {
            const color = e.detail.color;
            if (bgColorText) bgColorText.value = color.toUpperCase();
            updateBg();
        }) as EventListener);

        // Save actions
        this.app.container.querySelector('#sl-save-btn')?.addEventListener('click', () => {
            logDev('[LifeCycle] Triggering handleSave');
            this.app.handleSave();
        });

        this.app.container.querySelector('#sl-save-all-btn')?.addEventListener('click', () => {
            this.app.handleSaveAll();
        });

        bgColorText?.addEventListener('change', (e) => {
            let color = (e.target as HTMLInputElement).value;
            if (/^#[0-9A-F]{6}$/i.test(color)) {
                color = color.toUpperCase();
                bgColorText.value = color;
                bgColorInput.value = color;
                updatePickerBorder(color);
                updateBg();
            }
        });

        const backBtn = this.app.container.querySelector('#sl-back-btn') as HTMLButtonElement;
        const backDest = backBtn?.dataset.href || '/';
        backBtn?.addEventListener('click', () => {
            const redirect = () => {
                // Ensure the URL is absolute if it looks like a domain without a protocol
                let finalDest = backDest;
                if (finalDest !== '/' && !finalDest.startsWith('http://') && !finalDest.startsWith('https://')) {
                    finalDest = 'https://' + finalDest;
                }
                window.location.href = finalDest;
            };

            if (this.app.checkAnyDirty()) {
                const modal = this.app.container.querySelector('#sl-modal-unsaved-changes') as HTMLElement;
                modal.style.display = 'flex';
                const confirmBtn = this.app.container.querySelector('#sl-unsaved-confirm') as HTMLButtonElement;
                const cancelBtn = this.app.container.querySelector('#sl-unsaved-cancel') as HTMLButtonElement;
                const cleanup = () => {
                    confirmBtn.onclick = null;
                    cancelBtn.onclick = null;
                    modal.style.display = 'none';
                };
                confirmBtn.onclick = () => { cleanup(); redirect(); };
                cancelBtn.onclick = () => { cleanup(); };
            } else {
                redirect();
            }
        });

        window.onbeforeunload = (e) => {
            if (this.app.checkAnyDirty()) {
                e.preventDefault();
                e.returnValue = '';
                return '';
            }
        };

        this.app.container.addEventListener('click', (e) => {
            this.app.container.querySelectorAll('.sl-dropdown').forEach(d => d.classList.remove('open'));
            if (this.app.currentTool === 'scale') {
                const panel = this.app.container.querySelector('.sl-tool-panel.active');
                if (panel && !panel.contains(e.target as Node)) {
                    this.app.editorManager.switchTool(this.app.previousTool);
                }
            }
        });

        const refreshPreview = () => {
            if (this.app.currentBase64) this.app.updatePreview(this.app.currentBase64);
            this.app.historyManager.takeSnapshot();
        };
        this.app.container.querySelector('#sl-meta-width')?.addEventListener('change', refreshPreview);
        this.app.container.querySelector('#sl-meta-height')?.addEventListener('change', refreshPreview);
        this.app.container.querySelector('#sl-meta-frames')?.addEventListener('change', refreshPreview);

        const explorerPanel = this.app.container.querySelector('#sl-explorer-panel') as HTMLElement;
        explorerPanel?.addEventListener('mouseleave', () => {
            if (!this.app.itemId && this.app.lastItemId && this.app.lastProjectId) {
                if (this.app.projectId !== this.app.lastProjectId) this.app.projectId = this.app.lastProjectId;
                this.app.itemId = this.app.lastItemId;
                this.app.checkSpriteAvailability(false, true);
                this.renderExplorer();
            }
        });

        const sortSelect = this.app.container.querySelector('#sl-explorer-sort-select') as HTMLSelectElement;
        if (sortSelect) {
            sortSelect.value = `${this.app.sortConfig.key}:${this.app.sortConfig.order}`;
            sortSelect.addEventListener('change', () => {
                const [key, order] = sortSelect.value.split(':');
                this.app.sortConfig.key = key as any;
                this.app.sortConfig.order = order as any;
                this.renderExplorer();
            });
        }

        explorerPanel?.addEventListener('dragover', (e) => {
            e.preventDefault();
            if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
        });

        explorerPanel?.addEventListener('drop', async (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!e.dataTransfer) return;
            const type = e.dataTransfer.getData('sl/type');
            const id = e.dataTransfer.getData('sl/id');
            if (type === 'project' && id) {
                await this.app.copyProject(id);
            }
        });

        const tabsContainer = this.app.container.querySelector('#sl-sheet-tabs') as HTMLElement;
        if (tabsContainer) {
            tabsContainer.addEventListener('dragover', (e) => {
                e.preventDefault();
                if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
            });

            tabsContainer.addEventListener('drop', async (e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!e.dataTransfer) return;
                const type = e.dataTransfer.getData('sl/type');
                const id = e.dataTransfer.getData('sl/id');
                const sourceSprite = e.dataTransfer.getData('sl/sprite');
                const sourceProject = e.dataTransfer.getData('sl/project');

                if (type === 'sheet' && id && sourceSprite === this.app.itemId && sourceProject === this.app.projectId) {
                    await this.app.copySheet(this.app.projectId, this.app.itemId, id);
                }
            });
            const addBtnDrop = this.app.container.querySelector('#sl-add-sheet-btn') as HTMLElement;
        if (addBtnDrop) {
            addBtnDrop.addEventListener('dragover', (e) => {
                e.preventDefault();
                if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
            });
            addBtnDrop.addEventListener('drop', async (e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!e.dataTransfer) return;
                const type = e.dataTransfer.getData('sl/type');
                const id = e.dataTransfer.getData('sl/id');
                const sourceSprite = e.dataTransfer.getData('sl/sprite');
                const sourceProject = e.dataTransfer.getData('sl/project');
                if (type === 'sheet' && id && sourceSprite === this.app.itemId && sourceProject === this.app.projectId) {
                    await this.app.copySheet(this.app.projectId, this.app.itemId, id);
                }
            });
        }
    }

        const tabsScroll = this.app.container.querySelector('#sl-sheet-tabs-scroll') as HTMLElement;
        const tabsPrev = this.app.container.querySelector('#sl-tabs-prev') as HTMLElement;
        const tabsNext = this.app.container.querySelector('#sl-tabs-next') as HTMLElement;

        tabsPrev?.addEventListener('click', () => {
            tabsScroll.scrollBy({ left: -200, behavior: 'smooth' });
        });

        tabsNext?.addEventListener('click', () => {
            tabsScroll.scrollBy({ left: 200, behavior: 'smooth' });
        });

        tabsScroll?.addEventListener('scroll', () => this.updateTabArrows());

        window.addEventListener('resize', () => {
            this.updateTabArrows();
        });

        const trashBin = this.app.container.querySelector('#sl-trash-bin') as HTMLElement;
        if (trashBin) {
            trashBin.addEventListener('dragover', (e) => {
                e.preventDefault();
                e.stopPropagation();
                if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
                trashBin.classList.add('drag-over');
                trashBin.style.background = 'rgba(239, 68, 68, 0.4)';
                trashBin.style.transform = 'scale(1.1)';
                const icon = trashBin.querySelector('i');
                if (icon) icon.classList.add('fa-shake');
            });

            trashBin.addEventListener('dragleave', () => {
                trashBin.classList.remove('drag-over');
                trashBin.style.background = '';
                trashBin.style.transform = '';
                const icon = trashBin.querySelector('i');
                if (icon) icon.classList.remove('fa-shake');
            });

            trashBin.addEventListener('drop', async (e) => {
                e.preventDefault();
                e.stopPropagation();
                trashBin.classList.remove('drag-over');
                trashBin.style.background = '';
                trashBin.style.transform = '';
                const icon = trashBin.querySelector('i');
                if (icon) icon.classList.remove('fa-shake');
                if (!e.dataTransfer) return;

                const type = e.dataTransfer.getData('sl/type');
                const id = e.dataTransfer.getData('sl/id');
                const project = e.dataTransfer.getData('sl/project');

                if (type === 'sprite' && id && project) {
                    this.showConfirmModal('sprite', id, async () => {
                        if (!auth.currentUser) {
                            await GuestStorageManager.deleteSpriteKeys(project, id);
                            const proj = this.app.projectTree.find(p => p.name === project);
                            if (proj) proj.sprites = proj.sprites.filter(s => s !== id);
                            await this.app.loadProjectList();
                        } else {
                            if (await this.app.apiManager.deleteSprite(project, id)) {
                                await this.app.loadProjectList();
                            }
                        }
                    });
                } else if (type === 'project' && id) {
                    this.showConfirmModal('project', id, () => {
                        this.app.handleDeleteProject(id);
                    });
                } else if (type === 'sheet' && id && project) {
                    const sprite = e.dataTransfer.getData('sl/sprite');
                    this.showConfirmModal('sheet', id, async () => {
                        if (!auth.currentUser) {
                            const key = `${project}:${sprite}:${id}`;
                            await GuestStorageManager.deleteSheet(key);
                            await this.app.loadSpriteSheets(sprite);
                        } else {
                            if (await this.app.apiManager.deleteSheet(project, sprite, id)) {
                                await this.app.loadSpriteSheets(sprite);
                            }
                        }
                    });
                }
            });
        }
    }

    private setupAuth() {
        const authBtn = this.app.container.querySelector('#sl-auth-btn');
        authBtn?.addEventListener('click', () => {
            if (auth.currentUser) {
                if (confirm('Sign out?')) {
                    logout().then(() => this.app.loadSpriteList());
                }
            } else {
                const modal = new AuthModal({
                    onClose: () => modal.destroy(),
                    onSuccess: () => {
                        this.app.loadSpriteList();
                        modal.destroy();
                    }
                });
                document.body.appendChild(modal.getElement());
            }
        });

        // Only subscribe to Firebase auth changes on startup if the user has a prior session.
        // Guests (no session flag) skip Firebase entirely — zero SDK download, zero backend traffic.
        if (hasSession()) {
            subscribeToAuthChanges((user) => {
                this.handleAuthStateChange(user);
            });
        }
    }

    /** Called both by the Firebase auth listener and directly after a fresh login. */
    public handleAuthStateChange(user: import('firebase/auth').User | null) {
        console.log('[LifeCycleManager] onAuthStateChanged fired, user:', user ? user.email : 'null');
        const btn = this.app.container.querySelector('#sl-auth-btn') as HTMLElement;
        const span = btn?.querySelector('span');
        if (btn && span) {
            if (user) {
                span.innerText = user.email || 'User';
                btn.title = 'Click to Sign Out';
                btn.style.borderColor = 'rgba(99, 102, 241, 0.5)';
                btn.style.background = 'rgba(99, 102, 241, 0.1)';
            } else {
                span.innerText = this.app.t('label_sign_in');
                btn.title = 'Sign In';
                btn.style.borderColor = 'rgba(255,255,255,0.1)';
                btn.style.background = 'rgba(255,255,255,0.1)';
            }
        }
        if (user) {
            user.getIdToken().then(token => {
                const apiUrl = (import.meta as any).env.VITE_API_URL || '';
                console.log(`[LifeCycleManager] Syncing user with ${apiUrl}/api/users/sync`);
                fetch(`${apiUrl}/api/users/sync`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ idToken: token })
                }).then(() => {
                    console.log('[LifeCycleManager] User sync successful');
                }).catch(err => {
                    console.error('[LifeCycleManager] Failed to sync user:', err);
                });
            });
        }
        logDev('[LifeCycle] Auth state changed - force-reloading project list');
        this.app.loadProjectList(true);
        const mgmtPanel = this.app.container.querySelector('#sl-management-panel') as HTMLElement;
        if (mgmtPanel) mgmtPanel.style.display = user ? 'flex' : 'none';
    }

    private async setupPixi() {
        this.app.previewContainer = this.app.container.querySelector('#sl-preview-wrap') as HTMLDivElement;
        this.app.previewApp = new PIXI.Application({ width: 300, height: 300, backgroundAlpha: 0 });
        this.app.previewContainer!.appendChild(this.app.previewApp.view as any);
        (this.app.previewApp.view as any).style.touchAction = 'none';

        this.app.envContainer = new PIXI.Container();
        this.app.spriteContainer = new PIXI.Container();
        this.app.previewApp.stage.addChild(this.app.envContainer);
        this.app.previewApp.stage.addChild(this.app.spriteContainer);

        this.app.worldLayer = new PIXI.Container();
        this.app.worldLayer.sortableChildren = true;
        this.app.envContainer.addChild(this.app.worldLayer);

        window.addEventListener('resize', () => requestAnimationFrame(this.app.editorManager.resizeHandle));
        
        // Auto-transpose spritesheet when orientation changes
        window.matchMedia('(orientation: portrait)').addEventListener('change', (e) => {
            const isPortrait = e.matches;
            if (!this.app.dataCanvas) return;
            
            const fInput = this.app.container.querySelector('#sl-meta-frames') as HTMLInputElement;
            const count = parseInt(fInput?.value) || 1;
            
            // Only auto-transpose if there's more than 1 frame (layout orientation actually matters)
            if (count > 1) {
                const isHorz = this.app.dataCanvas.width >= this.app.dataCanvas.height;
                // Transpose if orientation is portrait but layout is horizontal
                // OR if orientation is landscape but layout is vertical
                if ((isPortrait && isHorz) || (!isPortrait && !isHorz)) {
                    this.app.editorManager.applyTranspose();
                }
            }
        });
        const resizeObserver = new ResizeObserver(() => requestAnimationFrame(this.app.editorManager.resizeHandle));
        resizeObserver.observe(this.app.previewContainer!);

        const sheetContainer = this.app.container.querySelector('#sl-sheet-container') as HTMLElement;
        let wasHidden = sheetContainer.clientWidth === 0;
        const sheetResizeObserver = new ResizeObserver(() => {
            if (this.app.currentBase64 && sheetContainer.clientWidth > 0 && wasHidden) {
                wasHidden = false;
                // If it was initialized while hidden (cW=0), auto-fit it now that it has a real size
                this.app.updatePreview(this.app.currentBase64, true);
            } else if (sheetContainer.clientWidth === 0) {
                wasHidden = true;
            }
        });
        sheetResizeObserver.observe(sheetContainer);

        const sheetFrame = this.app.container.querySelector('#sl-sheet-frame') as HTMLElement;
        this.app.editorManager.setupDOMPanZoom(sheetContainer, sheetFrame);
        this.app.editorManager.setupPixiPanZoom(this.app.previewContainer!);

        this.app.editorManager.updateEnvironment();
        this.app.editorManager.resizeHandle();
        this.app.editorManager.setupToolbar();
        this.app.editorManager.switchTool(this.app.currentTool);
    }

    public mount(parent: HTMLElement) {
        parent.appendChild(this.app.container);
        this.show();
    }

    public show() {
        this.app.container.style.display = 'flex';
        this.app.loadProjectList();
        this.updateMobileUI();
    }

    public hide() {
        this.app.container.style.display = 'none';
    }

    public updateMobileUI(forceScroll = true) {
        const isMobile = window.innerWidth <= 768;
        if (!isMobile) return;

        const sidebar = this.app.container.querySelector('#sl-sidebar-left') as HTMLElement;
        const main = this.app.container.querySelector('.sl-main-content-wrapper') as HTMLElement;

        if (this.app.itemId) {
            sidebar?.classList.remove('sl-active-panel');
            main?.classList.add('sl-active-panel');
        } else {
            sidebar?.classList.add('sl-active-panel');
            main?.classList.remove('sl-active-panel');
        }

        if (forceScroll) window.scrollTo(0, 0);
    }

    public renderExplorer() {
        logDev(`[LifeCycle] renderExplorer called with ${this.app.projectTree.length} projects`);
        if (!this.app.explorerTree) return;
        this.app.explorerTree.innerHTML = '';

        const sortedTree = [...this.app.projectTree].sort((a, b) => {
            if (this.app.sortConfig.key === 'name') {
                return this.app.sortConfig.order === 'asc' ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name);
            } else {
                const dateA = a.createdAt?.seconds || a.createdAt?._seconds || 0;
                const dateB = b.createdAt?.seconds || b.createdAt?._seconds || 0;
                return this.app.sortConfig.order === 'asc' ? dateA - dateB : dateB - dateA;
            }
        });

        if (sortedTree.length === 0 && this.app.projects.length > 0) {
            this.app.projectTree = this.app.projects.map((p: string) => ({ name: p, sprites: [] }));
        }

        const startRename = (element: HTMLElement, name: string, type: 'project' | 'sprite', parentId?: string) => {
            logDev(`[LifeCycle] startRename: ${type} - ${name}`, { parentId });
            const span = element.querySelector('.name-span') as HTMLElement;
            if (!span || element.querySelector('.sl-rename-input')) return;

            const input = document.createElement('input');
            input.className = 'sl-rename-input';
            input.value = name;

            // Seamless transition
            const rect = span.getBoundingClientRect();
            input.style.width = `${Math.max(rect.width, 50)}px`;

            // Prevent event bubbling to parent listeners
            const stop = (e: Event) => e.stopPropagation();
            input.addEventListener('mousedown', stop);
            input.addEventListener('click', stop);
            input.addEventListener('dblclick', stop);

            span.style.display = 'none';
            element.insertBefore(input, span);
            input.focus();
            input.select();

            const commit = async () => {
                if (input.dataset.committing) return;
                input.dataset.committing = 'true';

                const newName = input.value.trim();
                if (newName && newName !== name) {
                    const actualParentId = parentId || this.app.projectId;
                    const feedbackKey = type === 'sprite' ? `${actualParentId}:${newName}` : newName;
                    this.feedbackQueues[type].add(feedbackKey);

                    let success = false;
                    if (type === 'project') {
                        const result = await this.app.handleRenameProject(name, newName);
                        if (result && result !== newName) {
                            this.feedbackQueues.project.delete(newName);
                            this.feedbackQueues.project.add(result);
                        }
                        success = !!result;
                    } else {
                        success = await this.app.handleRenameSpriteInternal(name, newName, actualParentId);
                        if (!success) {
                            this.feedbackQueues.sprite.delete(feedbackKey);
                        }
                    }

                    if (input.parentNode) {
                        input.remove();
                        if (success) {
                            span.innerText = newName;
                            // Play feedback if this is the top bar (since it isn't rebuilt)
                            if (element.id === 'sl-sprite-title' || element.closest('#sl-sprite-title')) {
                                this.showFeedback(element);
                            }
                        }
                        span.style.display = 'inline';
                    }
                } else {
                    if (input.parentNode) input.remove();
                    span.style.display = 'inline';
                }
            };

            input.onblur = commit;
            input.onkeydown = (e) => {
                e.stopPropagation();
                if (e.key === 'Enter') commit();
                if (e.key === 'Escape') {
                    input.dataset.committing = 'true'; // Prevent blur-commit
                    input.remove();
                    span.style.display = 'inline';
                }
            };
        };

        sortedTree.forEach(project => {
            const projectItem = document.createElement('div');
            projectItem.className = `sl-explorer-item project ${this.app.projectId === project.name ? 'active' : ''} ${this.app.expandedProjects.has(project.name) ? 'expanded' : ''}`;
            projectItem.setAttribute('draggable', 'true');
            projectItem.innerHTML = `
                <span class="icon">${this.app.expandedProjects.has(project.name) ? '▾' : '▸'}</span>
                <span class="icon">📁</span>
                <span class="name-span">${project.name}</span>
                <div class="sl-item-menu">
                    <button class="menu-btn" title="${this.app.t('menu_title') || 'Menu'}" aria-label="Menu">
                        <i class="fa-solid fa-bars"></i>
                    </button>
                    <div class="sl-dropdown-menu">
                        <button class="sl-dropdown-item rename-btn" title="${this.app.t('action_rename') || 'Rename'}">
                            <i class="fa-solid fa-pen"></i>
                            <span>${this.app.t('action_rename') || 'Rename'}</span>
                        </button>
                        <button class="sl-dropdown-item duplicate-btn" title="${this.app.t('action_duplicate') || 'Duplicate'}">
                            <i class="fa-solid fa-copy"></i>
                            <span>${this.app.t('action_duplicate') || 'Duplicate'}</span>
                        </button>
                        <button class="sl-dropdown-item close" title="${this.app.t('action_delete') || 'Delete'}">
                            <i class="fa-solid fa-trash-can"></i>
                            <span>${this.app.t('action_delete') || 'Delete'}</span>
                        </button>
                    </div>
                </div>
            `;

            projectItem.addEventListener('dragstart', (e) => {
                e.stopPropagation();
                if (e.dataTransfer) {
                    e.dataTransfer.setData('sl/type', 'project');
                    e.dataTransfer.setData('sl/id', project.name);
                    e.dataTransfer.effectAllowed = 'copy';
                }
            });

            projectItem.addEventListener('dragover', (e) => {
                e.preventDefault();
                e.stopPropagation();
                if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
                projectItem.classList.add('drag-over');
            });

            projectItem.addEventListener('dragleave', () => {
                projectItem.classList.remove('drag-over');
            });

            projectItem.addEventListener('drop', async (e) => {
                e.preventDefault();
                e.stopPropagation();
                projectItem.classList.remove('drag-over');
                if (!e.dataTransfer) return;

                const type = e.dataTransfer.getData('sl/type');
                const id = e.dataTransfer.getData('sl/id');
                const sourceProject = e.dataTransfer.getData('sl/project');

                if (type === 'sprite' && id && sourceProject) {
                    await this.app.copySprite(sourceProject, id, project.name);
                } else if (type === 'project' && id) {
                    await this.app.copyProject(id);
                }
            });

            projectItem.querySelector('.icon:first-child')?.addEventListener('click', (e) => {
                e.stopPropagation();
                if (this.app.expandedProjects.has(project.name)) this.app.expandedProjects.delete(project.name);
                else this.app.expandedProjects.add(project.name);
                this.renderExplorer();
            });

            let projectClickTimeout: ReturnType<typeof setTimeout> | null = null;
            projectItem.addEventListener('click', (e) => {
                const target = e.target as HTMLElement;
                if (target.classList.contains('close') || target.closest('.close') || target.classList.contains('icon')) return;

                if (projectClickTimeout) {
                    clearTimeout(projectClickTimeout);
                    projectClickTimeout = null;
                }

                projectClickTimeout = setTimeout(() => {
                    if (this.app.expandedProjects.has(project.name)) {
                        this.app.expandedProjects.delete(project.name);
                    } else {
                        this.app.expandedProjects.add(project.name);
                    }
                    this.renderExplorer();
                }, 200);
            });

            projectItem.querySelector('.name-span')?.addEventListener('dblclick', (e) => {
                if (projectClickTimeout) {
                    clearTimeout(projectClickTimeout);
                    projectClickTimeout = null;
                }
                e.stopPropagation();
                startRename(projectItem, project.name, 'project');
            });

            projectItem.querySelector('.menu-btn')?.addEventListener('click', (e) => {
                e.stopPropagation();
                const btn = e.currentTarget as HTMLElement;
                const dropdown = projectItem.querySelector('.sl-dropdown-menu') as HTMLElement;
                const isShowing = dropdown?.classList.contains('show');
                document.querySelectorAll('.sl-item-menu .sl-dropdown-menu').forEach(m => {
                    m.classList.remove('show');
                    (m as HTMLElement).style.position = '';
                    (m as HTMLElement).style.top = '';
                    (m as HTMLElement).style.left = '';
                });
                if (!isShowing && dropdown) {
                    dropdown.classList.add('show');
                    dropdown.style.position = 'fixed';
                    dropdown.style.width = 'max-content';
                    const rect = btn.getBoundingClientRect();
                    dropdown.style.top = `${rect.bottom + 4}px`;
                    dropdown.style.left = 'auto';
                    dropdown.style.right = `${document.documentElement.clientWidth - rect.right}px`;
                }
            });

            projectItem.querySelector('.duplicate-btn')?.addEventListener('click', (e) => {
                e.stopPropagation();
                projectItem.querySelector('.sl-dropdown-menu')?.classList.remove('show');
                this.app.copyProject(project.name);
            });

            projectItem.querySelector('.rename-btn')?.addEventListener('click', (e) => {
                e.stopPropagation();
                projectItem.querySelector('.sl-dropdown-menu')?.classList.remove('show');
                projectItem.querySelector('.name-span')?.dispatchEvent(new MouseEvent('dblclick', { bubbles: true }));
            });

            projectItem.querySelector('.close')?.addEventListener('click', (e) => {
                e.stopPropagation();
                projectItem.querySelector('.sl-dropdown-menu')?.classList.remove('show');
                this.showConfirmModal('project', project.name, () => {
                    this.app.handleDeleteProject(project.name);
                });
            });

            this.app.explorerTree!.appendChild(projectItem);

            // Show feedback for project renaming (even if collapsed)
            if (this.feedbackQueues.project.has(project.name)) {
                this.showFeedback(projectItem);
                this.feedbackQueues.project.delete(project.name);
            }

            if (this.app.expandedProjects.has(project.name)) {
                project.sprites.forEach(sprite => {
                    const spriteItem = document.createElement('div');
                    const isActiveSprite = this.app.projectId === project.name && this.app.itemId === sprite;
                    spriteItem.className = `sl-explorer-item sprite ${isActiveSprite ? 'active' : ''}`;
                    spriteItem.setAttribute('draggable', 'true');
                    spriteItem.style.paddingLeft = '32px';
                    spriteItem.innerHTML = `
                        <span class="icon">🖼️</span>
                        <span class="name-span">${sprite}</span>
                        <div class="sl-item-menu">
                            <button class="menu-btn" title="${this.app.t('menu_title') || 'Menu'}" aria-label="Menu">
                                <i class="fa-solid fa-bars"></i>
                            </button>
                            <div class="sl-dropdown-menu">
                                <button class="sl-dropdown-item rename-btn" title="${this.app.t('action_rename') || 'Rename'}">
                                    <i class="fa-solid fa-pen"></i>
                                    <span>${this.app.t('action_rename') || 'Rename'}</span>
                                </button>
                                <button class="sl-dropdown-item duplicate-btn" title="${this.app.t('action_duplicate') || 'Duplicate'}">
                                    <i class="fa-solid fa-copy"></i>
                                    <span>${this.app.t('action_duplicate') || 'Duplicate'}</span>
                                </button>
                                <button class="sl-dropdown-item close" title="${this.app.t('action_delete') || 'Delete'}">
                                    <i class="fa-solid fa-trash-can"></i>
                                    <span>${this.app.t('action_delete') || 'Delete'}</span>
                                </button>
                            </div>
                        </div>
                    `;

                    spriteItem.addEventListener('dragstart', (e) => {
                        e.stopPropagation();
                        if (e.dataTransfer) {
                            e.dataTransfer.setData('sl/type', 'sprite');
                            e.dataTransfer.setData('sl/id', sprite);
                            e.dataTransfer.setData('sl/project', project.name);
                            e.dataTransfer.effectAllowed = 'copy';
                        }
                    });

                    spriteItem.addEventListener('dragover', (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
                    });

                    spriteItem.addEventListener('drop', async (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (!e.dataTransfer) return;

                        const type = e.dataTransfer.getData('sl/type');
                        const id = e.dataTransfer.getData('sl/id');
                        const sourceProject = e.dataTransfer.getData('sl/project');

                        if (type === 'sprite' && id) {
                            await this.app.copySprite(sourceProject, id, project.name);
                        }
                    });

                    let spriteClickTimeout: ReturnType<typeof setTimeout> | null = null;
                    spriteItem.addEventListener('click', (e) => {
                        const target = e.target as HTMLElement;
                        if (target.classList.contains('close') || target.closest('.close')) return;

                        if (spriteClickTimeout) {
                            clearTimeout(spriteClickTimeout);
                            spriteClickTimeout = null;
                        }

                        spriteClickTimeout = setTimeout(() => {
                            if (this.app.projectId !== project.name || this.app.itemId !== sprite) {
                                if (this.app.itemId) {
                                    this.app.saveSheetToMemory();
                                }
                                this.app.projectId = project.name;
                                this.app.itemId = sprite;
                                this.app.checkSpriteAvailability();
                                this.renderExplorer();
                                this.updateMobileUI();
                            }
                        }, 200);
                    });

                    spriteItem.querySelector('.name-span')?.addEventListener('dblclick', (e) => {
                        if (spriteClickTimeout) {
                            clearTimeout(spriteClickTimeout);
                            spriteClickTimeout = null;
                        }
                        e.stopPropagation();
                        startRename(spriteItem, sprite, 'sprite', project.name);
                    });

                    spriteItem.querySelector('.menu-btn')?.addEventListener('click', (e) => {
                        e.stopPropagation();
                        const btn = e.currentTarget as HTMLElement;
                        const dropdown = spriteItem.querySelector('.sl-dropdown-menu') as HTMLElement;
                        const isShowing = dropdown?.classList.contains('show');
                        document.querySelectorAll('.sl-item-menu .sl-dropdown-menu').forEach(m => {
                            m.classList.remove('show');
                            (m as HTMLElement).style.position = '';
                            (m as HTMLElement).style.top = '';
                            (m as HTMLElement).style.left = '';
                        });
                        if (!isShowing && dropdown) {
                            dropdown.classList.add('show');
                            dropdown.style.position = 'fixed';
                            dropdown.style.width = 'max-content';
                            const rect = btn.getBoundingClientRect();
                            dropdown.style.top = `${rect.bottom + 4}px`;
                            dropdown.style.left = 'auto';
                            dropdown.style.right = `${document.documentElement.clientWidth - rect.right}px`;
                        }
                    });

                    spriteItem.querySelector('.duplicate-btn')?.addEventListener('click', (e) => {
                        e.stopPropagation();
                        spriteItem.querySelector('.sl-dropdown-menu')?.classList.remove('show');
                        this.app.copySprite(project.name, sprite, project.name);
                    });

                    spriteItem.querySelector('.rename-btn')?.addEventListener('click', (e) => {
                        e.stopPropagation();
                        spriteItem.querySelector('.sl-dropdown-menu')?.classList.remove('show');
                        spriteItem.querySelector('.name-span')?.dispatchEvent(new MouseEvent('dblclick', { bubbles: true }));
                    });

                    spriteItem.querySelector('.close')?.addEventListener('click', (e) => {
                        e.stopPropagation();
                        spriteItem.querySelector('.sl-dropdown-menu')?.classList.remove('show');
                        this.showConfirmModal('sprite', sprite, async () => {
                            if (!auth.currentUser) {
                                try {
                                    await GuestStorageManager.deleteSpriteKeys(project.name, sprite);
                                } catch (err) {
                                    console.error('[SpriteLab] IDB deleteSprite failed:', err);
                                }
                                const proj = this.app.projectTree.find(p => p.name === project.name);
                                if (proj) proj.sprites = proj.sprites.filter(s => s !== sprite);

                                const prefix = `${project.name}:${sprite}:`;
                                for (const key of this.app.sessionSheets.keys()) {
                                    if (key.startsWith(prefix)) this.app.sessionSheets.delete(key);
                                }
                                if (this.app.itemId === sprite) {
                                    this.app.itemId = proj && proj.sprites.length > 0 ? proj.sprites[0] : '';
                                    this.app.currentSheet = 'main';
                                    if (!this.app.itemId) {
                                        const canvas = this.app.container.querySelector('#sl-sheet-canvas') as HTMLCanvasElement;
                                        const ctx = canvas?.getContext('2d');
                                        if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
                                        this.app.renderHistoryList();
                                    } else {
                                        this.app.checkSpriteAvailability();
                                    }
                                }
                                await this.app.loadProjectList();
                                return;
                            }

                            if (await this.app.apiManager.deleteSprite(project.name, sprite)) {
                                await this.app.loadProjectList();
                            }
                        });
                    });

                    this.app.explorerTree!.appendChild(spriteItem);

                    if (this.feedbackQueues.sprite.has(`${project.name}:${sprite}`)) {
                        this.showFeedback(spriteItem);
                        this.feedbackQueues.sprite.delete(`${project.name}:${sprite}`);
                    }
                });

                // Add "New Sprite" button for this project
                const newSpriteBtn = document.createElement('div');
                newSpriteBtn.className = 'sl-explorer-item sprite sl-explorer-add-btn';
                newSpriteBtn.innerHTML = `
                    <span class="icon" style="font-size: 16px; font-weight: bold;">+</span>
                    <span class="name-span" style="font-weight: 600;">${this.app.t('action_new_sprite')}</span>
                `;

                newSpriteBtn.addEventListener('click', () => {
                    this.app.projectId = project.name; // Set context for new sprite
                    const modal = this.app.container.querySelector('#sl-modal-create-sprite') as HTMLElement;
                    if (modal) {
                        modal.style.display = 'flex';
                        const input = this.app.container.querySelector('#sl-new-sprite-name') as HTMLInputElement;
                        if (input) input.focus();
                    }
                });

                newSpriteBtn.addEventListener('dragover', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
                });

                newSpriteBtn.addEventListener('drop', async (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!e.dataTransfer) return;

                    const type = e.dataTransfer.getData('sl/type');
                    const id = e.dataTransfer.getData('sl/id');
                    const sourceProject = e.dataTransfer.getData('sl/project');

                    if (type === 'sprite' && id) {
                        await this.app.copySprite(sourceProject, id, project.name);
                    }
                });

                this.app.explorerTree!.appendChild(newSpriteBtn);
            }
        });

        // Add "New Project" button at the bottom
        const newProjectBtn = document.createElement('div');
        newProjectBtn.className = 'sl-explorer-item sl-explorer-add-btn';
        newProjectBtn.innerHTML = `
            <span class="icon" style="font-size: 16px; font-weight: bold;">+</span>
            <span class="name-span" style="font-weight: 600;">${this.app.t('action_new_project')}</span>
        `;

        newProjectBtn.addEventListener('click', () => {
            const modal = this.app.container.querySelector('#sl-modal-create-project') as HTMLElement;
            if (modal) {
                modal.style.display = 'flex';
                const input = this.app.container.querySelector('#sl-new-project-name') as HTMLInputElement;
                if (input) input.focus();
            }
        });

        this.app.explorerTree!.appendChild(newProjectBtn);
    }

    private updateTabArrows() {
        const tabsScroll = this.app.container.querySelector('#sl-sheet-tabs-scroll') as HTMLElement;
        const tabsPrev = this.app.container.querySelector('#sl-tabs-prev') as HTMLElement;
        const tabsNext = this.app.container.querySelector('#sl-tabs-next') as HTMLElement;

        if (!tabsScroll || !tabsPrev || !tabsNext) return;

        const { scrollLeft, scrollWidth, clientWidth } = tabsScroll;
        tabsPrev.style.display = scrollLeft > 2 ? 'flex' : 'none';
        tabsNext.style.display = (scrollLeft + clientWidth) < (scrollWidth - 2) ? 'flex' : 'none';
    }

    private scrollToActiveTab() {
        const tabsScroll = this.app.container.querySelector('#sl-sheet-tabs-scroll') as HTMLElement;
        const activeTab = tabsScroll?.querySelector('.sl-sheet-tab.active') as HTMLElement;
        if (tabsScroll && activeTab) {
            tabsScroll.scrollTo({ left: activeTab.offsetLeft - 20, behavior: 'smooth' });
        }
    }

    public renderTabs(states: string[]) {
        const tabsContainer = this.app.container.querySelector('#sl-sheet-tabs') as HTMLElement;
        if (!tabsContainer) return;

        tabsContainer.innerHTML = '';

        states.forEach(state => {
            const tab = document.createElement('div');
            const isActive = this.app.currentSheet === state;
            tab.className = `sl-sheet-tab ${isActive ? 'active' : ''}`;
            tab.setAttribute('draggable', 'true');

            tab.innerHTML = `<span class="name-span">${state}</span>
                <div class="sl-item-menu">
                    <button class="menu-btn" title="${this.app.t('menu_title') || 'Menu'}" aria-label="Menu">
                        <i class="fa-solid fa-bars"></i>
                    </button>
                    <div class="sl-dropdown-menu">
                        <button class="sl-dropdown-item rename-btn" title="${this.app.t('action_rename') || 'Rename'}">
                            <i class="fa-solid fa-pen"></i>
                            <span>${this.app.t('action_rename') || 'Rename'}</span>
                        </button>
                        <button class="sl-dropdown-item duplicate-btn" title="${this.app.t('action_duplicate') || 'Duplicate'}">
                            <i class="fa-solid fa-copy"></i>
                            <span>${this.app.t('action_duplicate') || 'Duplicate'}</span>
                        </button>
                        <button class="sl-dropdown-item close" title="${this.app.t('action_delete') || 'Delete'}">
                            <i class="fa-solid fa-trash-can"></i>
                            <span>${this.app.t('action_delete') || 'Delete'}</span>
                        </button>
                    </div>
                </div>`;

            tab.addEventListener('dragstart', (e) => {
                e.stopPropagation();
                if (e.dataTransfer) {
                    e.dataTransfer.setData('sl/type', 'sheet');
                    e.dataTransfer.setData('sl/id', state);
                    e.dataTransfer.setData('sl/sprite', this.app.itemId);
                    e.dataTransfer.setData('sl/project', this.app.projectId);
                    e.dataTransfer.effectAllowed = 'copy';
                }
            });

            tab.addEventListener('dragover', (e) => {
                e.preventDefault();
                e.stopPropagation();
                if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
            });

            tab.addEventListener('drop', async (e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!e.dataTransfer) return;

                const type = e.dataTransfer.getData('sl/type');
                const id = e.dataTransfer.getData('sl/id');
                const sourceSprite = e.dataTransfer.getData('sl/sprite');
                const sourceProject = e.dataTransfer.getData('sl/project');

                if (type === 'sheet' && id && sourceSprite === this.app.itemId && sourceProject === this.app.projectId) {
                    await this.app.copySheet(this.app.projectId, this.app.itemId, id);
                }
            });

            tab.addEventListener('click', (e) => {
                const target = e.target as HTMLElement;
                if (target.classList.contains('close') || target.closest('.close')) return;
                if (this.app.currentSheet !== state) {
                    this.app.saveSheetToMemory();
                    this.app.currentSheet = state;
                    this.app.checkSpriteAvailability(true); // Fetches data if not in memory
                    this.renderTabs(states); // Re-render to show active tab immediately
                }
            });

            tab.querySelector('span')?.addEventListener('dblclick', (e) => {
                e.stopPropagation();
                const span = e.target as HTMLElement;
                if (tab.querySelector('.sl-tab-rename-input')) return;

                const input = document.createElement('input');
                input.value = state;
                input.className = 'sl-tab-rename-input';

                const rect = span.getBoundingClientRect();
                input.style.width = `${Math.max(rect.width, 40)}px`;

                const stop = (ev: Event) => ev.stopPropagation();
                input.addEventListener('mousedown', stop);
                input.addEventListener('click', stop);
                input.addEventListener('dblclick', stop);

                span.style.display = 'none';
                tab.insertBefore(input, span);
                input.focus();
                input.select();

                let committed = false;
                const commit = async () => {
                    if (committed) return;
                    committed = true;

                    const newName = input.value.trim();
                    if (newName && newName !== state) {
                        const feedbackKey = `${this.app.projectId}:${this.app.itemId}:${newName}`;
                        this.feedbackQueues.sheet.add(feedbackKey);

                        let success = false;
                        if (!auth.currentUser) {
                            // Guest: rename in IDB and locally
                            try {
                                await GuestStorageManager.renameSheetKeys(this.app.projectId, this.app.itemId, state, newName);
                                success = true;
                            } catch (e) {
                                console.error('[SpriteLab] IDB tab rename failed:', e);
                            }
                        } else {
                            success = await this.app.apiManager.renameSheet(this.app.projectId, this.app.itemId, state, newName);
                        }

                        if (success) {
                            const oldKey = `${this.app.projectId}:${this.app.itemId}:${state}`;
                            const newKey = `${this.app.projectId}:${this.app.itemId}:${newName}`;
                            if (this.app.sessionSheets.has(oldKey)) {
                                this.app.sessionSheets.set(newKey, this.app.sessionSheets.get(oldKey)!);
                                this.app.sessionSheets.delete(oldKey);
                            }
                            this.app.currentSheet = newName;
                            // Update parsedSheets to prevent api fetch fallback missing the rename
                            const i = this.app.parsedSheets.indexOf(state);
                            if (i >= 0) this.app.parsedSheets[i] = newName;

                            await this.app.loadSpriteSheets(this.app.itemId);
                            this.app.checkSpriteAvailability(true);
                        } else {
                            this.feedbackQueues.sheet.delete(feedbackKey);
                            input.remove();
                            span.style.display = 'inline';
                        }
                    } else {
                        input.remove();
                        span.style.display = 'inline';
                    }
                };
                input.onblur = commit;
                input.onkeydown = (ev) => {
                    ev.stopPropagation();
                    if (ev.key === 'Enter') {
                        ev.preventDefault();
                        commit();
                    }
                    if (ev.key === 'Escape') {
                        committed = true;
                        input.remove();
                        span.style.display = 'inline';
                    }
                };
            });

            if (this.feedbackQueues.sheet.has(`${this.app.projectId}:${this.app.itemId}:${state}`)) {
                this.showFeedback(tab);
                this.feedbackQueues.sheet.delete(`${this.app.projectId}:${this.app.itemId}:${state}`);
            }

            tab.querySelector('.menu-btn')?.addEventListener('click', (e) => {
                e.stopPropagation();
                const btn = e.currentTarget as HTMLElement;
                const dropdown = tab.querySelector('.sl-dropdown-menu') as HTMLElement;
                const isShowing = dropdown?.classList.contains('show');
                document.querySelectorAll('.sl-item-menu .sl-dropdown-menu').forEach(m => {
                    m.classList.remove('show');
                    (m as HTMLElement).style.position = '';
                    (m as HTMLElement).style.top = '';
                    (m as HTMLElement).style.left = '';
                });
                if (!isShowing && dropdown) {
                    dropdown.classList.add('show');
                    dropdown.style.position = 'fixed';
                    dropdown.style.width = 'max-content';
                    const rect = btn.getBoundingClientRect();
                    dropdown.style.top = `${rect.bottom + 4}px`;
                    dropdown.style.left = 'auto';
                    dropdown.style.right = `${document.documentElement.clientWidth - rect.right}px`;
                }
            });

            tab.querySelector('.duplicate-btn')?.addEventListener('click', (e) => {
                e.stopPropagation();
                tab.querySelector('.sl-dropdown-menu')?.classList.remove('show');
                this.app.copySheet(this.app.projectId, this.app.itemId, state);
            });

            tab.querySelector('.rename-btn')?.addEventListener('click', (e) => {
                e.stopPropagation();
                tab.querySelector('.sl-dropdown-menu')?.classList.remove('show');
                tab.querySelector('.name-span')?.dispatchEvent(new MouseEvent('dblclick', { bubbles: true }));
            });

            tab.querySelector('.close')?.addEventListener('click', (e) => {
                e.stopPropagation();
                tab.querySelector('.sl-dropdown-menu')?.classList.remove('show');
                const isLastSheet = states.length <= 1;
                this.showConfirmModal('sheet', state, async () => {
                    if (!auth.currentUser) {
                        try {
                            const key = `${this.app.projectId}:${this.app.itemId}:${state}`;
                            await GuestStorageManager.deleteSheet(key);
                            this.app.sessionSheets.delete(key);
                            
                            if (isLastSheet) {
                                const newSheetName = 'main';
                                const newKey = `${this.app.projectId}:${this.app.itemId}:${newSheetName}`;
                                const gw = parseInt((document.getElementById('sl-meta-width') as HTMLInputElement)?.value) || 32;
                                const gh = parseInt((document.getElementById('sl-meta-height') as HTMLInputElement)?.value) || 32;
                                const tmpCanvas = document.createElement('canvas');
                                tmpCanvas.width = gw; tmpCanvas.height = gh;
                                const blankB64 = tmpCanvas.toDataURL();
                                await GuestStorageManager.saveSheet({
                                    key: newKey,
                                    base64: blankB64,
                                    originalBase64: blankB64,
                                    width: gw, height: gh, frames: 1, orientation: 'horizontal',
                                    animation: 'sequence', interval: 0.2,
                                    palette: [...(this.app.colorPicker?.getPalette() || [])],
                                    savedAt: Date.now()
                                });
                                this.app.currentSheet = newSheetName;
                            } else if (this.app.currentSheet === state) {
                                const next = states.find(s => s !== state);
                                this.app.currentSheet = next || 'main';
                            }
                            
                            await this.app.loadSpriteSheets(this.app.itemId);
                            this.app.checkSpriteAvailability(true);
                        } catch (err) {
                            console.error('[SpriteLab] IDB deleteSheet failed:', err);
                        }
                        return;
                    }

                    const success = await this.app.apiManager.deleteSheet(this.app.projectId, this.app.itemId, state);
                    if (success) {
                        this.app.sessionSheets.delete(`${this.app.projectId}:${this.app.itemId}:${state}`);
                        
                        if (isLastSheet) {
                            const newSheetName = 'main';
                            const idToken = await auth.currentUser.getIdToken();
                            const apiUrl = (import.meta as any).env.VITE_API_URL || '';
                            const gw = parseInt((document.getElementById('sl-meta-width') as HTMLInputElement)?.value) || 32;
                            const gh = parseInt((document.getElementById('sl-meta-height') as HTMLInputElement)?.value) || 32;
                            await fetch(`${apiUrl}/api/sprite-lab/projects/${this.app.projectId}/sprites/${this.app.itemId}/sheets`, {
                                method: 'POST',
                                headers: { 'Authorization': `Bearer ${idToken}`, 'Content-Type': 'application/json' },
                                body: JSON.stringify({ name: newSheetName, width: gw, height: gh, frames: 1, orientation: 'horizontal' })
                            });
                            this.app.currentSheet = newSheetName;
                        } else if (this.app.currentSheet === state) {
                            const next = states.find(s => s !== state);
                            this.app.currentSheet = next || 'main';
                        }
                        
                        await this.app.loadSpriteSheets(this.app.itemId);
                        this.app.checkSpriteAvailability(true);
                    }
                });
            });

            tabsContainer.appendChild(tab);
        });

        requestAnimationFrame(() => {
            this.updateTabArrows();
            this.scrollToActiveTab();
        });
    }

    public renderHistoryList() {
        const list = this.app.container.querySelector('#sl-history-list') as HTMLElement;
        if (!list) return;
        if (!this.app.itemId || !this.app.currentSheet) { list.innerHTML = ''; return; }

        const key = `${this.app.projectId}:${this.app.itemId}:${this.app.currentSheet}`;
        const sheetData = this.app.sessionSheets.get(key);
        list.innerHTML = '';
        if (!sheetData || !sheetData.history || sheetData.history.length === 0) return;

        sheetData.history.forEach((snapshot: SnapshotData, index: number) => {
            const item = document.createElement('div');
            item.className = 'sl-history-item';

            let shortcut = '';
            if (index === sheetData.historyIndex) {
                item.classList.add('active');
            } else if (index < sheetData.historyIndex) {
                shortcut = 'Ctrl+Z';
            } else {
                shortcut = 'Ctrl+Y';
                item.classList.add('future');
            }

            const thumb = document.createElement('div');
            thumb.className = 'sl-history-thumb';
            thumb.style.backgroundImage = `url(${snapshot.base64})`;
            thumb.style.backgroundSize = 'contain';
            thumb.style.backgroundPosition = 'center';
            thumb.style.backgroundRepeat = 'no-repeat';

            const label = document.createElement('div');
            label.className = 'sl-history-label';
            let text = `Snapshot ${index + 1}`;
            const prev = index > 0 ? sheetData.history[index - 1] : null;
            if (!prev || prev.width !== snapshot.width || prev.height !== snapshot.height) {
                text += ` (${snapshot.width}x${snapshot.height})`;
            }
            if (index === 0) text += ' (Original)';
            if (index === sheetData.historyIndex) text += ' (Current)';
            label.innerText = text;

            item.appendChild(thumb);
            item.appendChild(label);

            if (shortcut) {
                const hint = document.createElement('span');
                hint.className = 'sl-history-shortcut';
                hint.innerText = shortcut;
                item.appendChild(hint);
            }

            item.addEventListener('click', async () => {
                await this.app.historyManager.loadHistorySnapshot(index);
            });
            list.appendChild(item);
        });

        const activeItem = list.querySelector('.active') as HTMLElement;
        if (activeItem) {
            list.scrollTo({ top: activeItem.offsetTop - list.clientHeight / 2 + activeItem.clientHeight / 2, behavior: 'smooth' });
        }
    }

    public updateButtonStates() {
        const saveBtn = this.app.container.querySelector('#sl-save-btn') as HTMLButtonElement;
        const saveAllBtn = this.app.container.querySelector('#sl-save-all-btn') as HTMLButtonElement;
        const isDirty = this.app.checkDirty();
        const anyDirty = this.app.checkAnyDirty();

        if (saveBtn) saveBtn.classList.toggle('dirty', isDirty);
        if (saveAllBtn) saveAllBtn.classList.toggle('dirty', anyDirty);
    }

    private showConfirmModal(type: 'project' | 'sprite' | 'sheet', name: string, onConfirm: () => void) {
        const modalId = `#sl-modal-delete-${type}`;
        const nameDispId = `#sl-delete-${type}-name-disp`;
        const confirmBtnId = `#sl-delete-${type}-confirm`;
        const cancelBtnId = `#sl-delete-${type}-cancel`;

        const modal = this.app.container.querySelector(modalId) as HTMLElement;
        const nameDisp = this.app.container.querySelector(nameDispId) as HTMLElement;
        const confirmBtn = this.app.container.querySelector(confirmBtnId) as HTMLButtonElement;
        const cancelBtn = this.app.container.querySelector(cancelBtnId) as HTMLButtonElement;

        if (!modal || !confirmBtn || !cancelBtn) return;

        if (nameDisp) nameDisp.textContent = name;
        modal.style.display = 'flex';

        const cleanup = () => {
            modal.style.display = 'none';
            confirmBtn.onclick = null;
            cancelBtn.onclick = null;
        };

        confirmBtn.onclick = () => {
            cleanup();
            onConfirm();
        };

        cancelBtn.onclick = () => {
            cleanup();
        };
    }

    private async processGifFile(file: File) {
        logDev(`[LifeCycle] Processing GIF file: ${file.name}`);
        const buffer = await file.arrayBuffer();
        const parsedGif = parseGIF(buffer) as any;
        const frames = decompressFrames(parsedGif, true);

        if (!frames || frames.length === 0) {
            alert('Failed to decode GIF frames.');
            return;
        }

        const delays = frames.map(f => f.delay).filter(d => d > 0);
        const minDelay = delays.length > 0 ? Math.min(...delays) : 100;
        let baseInterval = Math.max(10, Math.round(minDelay / 10) * 10);
        
        let newFramesCount = 0;
        frames.forEach(frame => {
            const count = frame.delay > 0 ? Math.max(1, Math.round(frame.delay / baseInterval)) : 1;
            newFramesCount += count;
        });

        let frameW = parsedGif.lsd?.width;
        let frameH = parsedGif.lsd?.height;
        if (!frameW || !frameH) {
            frameW = frames[0]?.dims?.width || 32;
            frameH = frames[0]?.dims?.height || 32;
        }

        logDev(`[LifeCycle] GIF Dimensions: ${frameW}x${frameH}, frames: ${frames.length}, newFramesCount: ${newFramesCount}`);

        const isMobilePortrait = window.innerWidth < 768 && window.innerHeight > window.innerWidth;
        const newOrientation = isMobilePortrait ? 'vertical' : 'horizontal';
        const newWidth = isMobilePortrait ? frameW : frameW * newFramesCount;
        const newHeight = isMobilePortrait ? frameH * newFramesCount : frameH;

        const canvas = document.createElement('canvas');
        canvas.width = newWidth;
        canvas.height = newHeight;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (!ctx) {
            logDev(`[LifeCycle] Failed to get final canvas context`);
            return;
        }

        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = frameW;
        tempCanvas.height = frameH;
        const tempCtx = tempCanvas.getContext('2d', { willReadFrequently: true });
        if (!tempCtx) return;

        let cursorX = 0;
        let cursorY = 0;
        let backupData: ImageData | null = null;

        for (let i = 0; i < frames.length; i++) {
            const frame = frames[i];
            const dims = frame.dims;

            if (frame.disposalType === 3) {
                backupData = tempCtx.getImageData(0, 0, frameW, frameH);
            }

            try {
                if (frame.dims.width > 0 && frame.dims.height > 0) {
                    const patchData = new ImageData(
                        new Uint8ClampedArray(frame.patch),
                        frame.dims.width,
                        frame.dims.height
                    );
                    const patchCanvas = document.createElement('canvas');
                    patchCanvas.width = frame.dims.width;
                    patchCanvas.height = frame.dims.height;
                    patchCanvas.getContext('2d')!.putImageData(patchData, 0, 0);
                    tempCtx.drawImage(patchCanvas, frame.dims.left, frame.dims.top);
                }
            } catch (err: any) {
                logDev(`[LifeCycle] Error drawing frame ${i}: ${err.message}`);
            }

            const count = frame.delay > 0 ? Math.max(1, Math.round(frame.delay / baseInterval)) : 1;
            for (let k = 0; k < count; k++) {
                if (cursorX < newWidth && cursorY < newHeight) {
                    ctx.drawImage(tempCanvas, cursorX, cursorY);
                }
                if (newOrientation === 'horizontal') {
                    cursorX += frameW;
                } else {
                    cursorY += frameH;
                }
            }

            if (frame.disposalType === 2) {
                tempCtx.clearRect(dims.left, dims.top, dims.width, dims.height);
            } else if (frame.disposalType === 3 && backupData) {
                tempCtx.putImageData(backupData, 0, 0);
            }
        }

        const imgData = ctx.getImageData(0, 0, newWidth, newHeight);
        const data = imgData.data;
        for (let i = 0; i < data.length; i += 4) {
            if (data[i] === 255 && data[i + 1] === 0 && data[i + 2] === 255) {
                data[i + 3] = 0;
            }
        }
        ctx.putImageData(imgData, 0, 0);

        const finalBase64 = canvas.toDataURL('image/png');
        logDev(`[LifeCycle] GIF processed. newFrames: ${newFramesCount}, w: ${frameW}, h: ${frameH}, finalBase64 len: ${finalBase64.length}`);

        (this.app.container.querySelector('#sl-meta-width') as HTMLInputElement).value = frameW.toString();
        (this.app.container.querySelector('#sl-meta-height') as HTMLInputElement).value = frameH.toString();
        (this.app.container.querySelector('#sl-meta-frames') as HTMLInputElement).value = newFramesCount.toString();
        const orientSelect = this.app.container.querySelector('#sl-meta-orientation') as HTMLSelectElement;
        if (orientSelect) orientSelect.value = newOrientation;

        const sheetData = this.app.sessionSheets.get(`${this.app.projectId}:${this.app.itemId}:${this.app.currentSheet}`);
        if (sheetData) {
            sheetData.interval = baseInterval / 1000;
            sheetData.animation = 'sequence';
        }

        await this.app.updatePreview(finalBase64);
        this.app.historyManager.takeSnapshot();
        this.app.updateAnimation();
    }
}




